<template>
    <div v-if="goalTitle">
        <breadcrumb :items="breadcrumbs" />
        <v-container fluid>
            <!-- Header Section -->
            <div class="d-flex mb-8 flex-column flex-sm-row">
                <div class="goal-title-container">
                    <h2 class="goal-title">{{ goalTitle || $t('esgPlatform.surveyResponse.loadingGoalTitle') }}</h2>
                </div>
                <v-spacer />
                <div class="d-flex mt-4 mt-sm-0">
                    <v-btn color="primary" class="mr-2" @click="goToSurveyOverview">
                        {{ $t('esgPlatform.esgReport.back') }}
                    </v-btn>
                    <v-btn v-if="showSubmitActionButton" color="primary" dark class="mr-2"
                        @click="openConfirmBox = true; message = $t('esgPlatform.confirmSurveyResponse'); confirmAction = submitEntireSurvey" :disabled="isSubmitDisabled">
                        {{ showSubmitAgainButton ? $t('esgPlatform.surveyResponse.submitAgain') : $t('esgPlatform.surveyResponse.submit') }}
                    </v-btn>
                </div>
            </div>

            <v-row class="repeat-layout-row">
                <v-col :cols="12" :md="hasRepeatSubmissions ? 8 : 12" class="repeat-main-col">
                    <v-card elevation="0">
                <div class="survey-content" ref="surveyContent">
                    <!-- Question Header and Navigation -->
                    <div class="d-flex flex-column flex-sm-row align-center">
                        <v-col cols="12" sm="6">
                            <label class="font-weight-bold question-heading">
                                {{ currentQuestion?.questionHeading?.title || $t('esgPlatform.surveyResponse.question')}}
                            </label>
                        </v-col>

                        <v-col v-if="!hasRepeatSubmissions && displaySubmittedOn" cols="12" sm="2" class="ml-auto text-right">
                            <label class="font-weight-bold">
                                <p class="question-text">{{ $t('esgPlatform.submittedOn') }}:</p>
                                <span>{{ formatWithMoment(displaySubmittedOn) }}</span>
                            </label>
                        </v-col>
                    </div>
                    <v-row class="mt-6">
                        <v-col cols="12" class="survey-form-col">
                            <v-form ref="surveyForm" v-model="formValid" :class="['survey-question', { 'repeat-read-only': isRepeatReadOnly }]">
                            <p class="font-weight-bold">
                                {{ $t('esgPlatform.surveyResponse.questionOf', {
                                    current: currentQuestionNumber,
                                    total: currentQuestion.order
                                })
                                }}
                            </p>
                            <p class="question-text">{{ currentQuestion?.title }}</p>

                            <p v-if="currentQuestion?.additionalSetting?.description" class="question-description"
                                v-html="currentQuestion.additionalSetting.description">
                            </p>
                            <div v-if="currentQuestion && submittedRejectionReason" class="rejection-block">
                                <div class="rejection-title">{{ $t('esgPlatform.surveyResponse.submissionRejected') }}</div>
                                <span>{{ $t('esgPlatform.surveyResponse.rejectionReason') }}:</span>
                                <div class="rejection-reason">{{ submittedRejectionReason }}</div>
                            </div>
                            <div v-if="repeatPlaceholderText" class="repeat-placeholder">
                                {{ repeatPlaceholderText }}
                            </div>
                            <divider/>
                            <!-- Separate Farms -->
                            <div v-if="isSeparateResponseQuestion && availableFarms.length > 0" class="my-4">
                                <v-divider class="mb-4"></v-divider>
                                
                                <div class="d-flex flex-column align-center">
                                    <div class="d-flex align-center justify-center">
                                        <v-btn 
                                            icon 
                                            small
                                            @click="goToPreviousFarm" 
                                            :disabled="currentFarmIndex === 0"
                                            class="ma-1 arrow-button"
                                        >
                                            <v-icon :color="currentFarmIndex === 0 ? 'grey' : '#FFFFFF'">mdi-chevron-left</v-icon>
                                        </v-btn>
                                        <h4 class="ma-0 px-4">{{ currentFarm?.farmName }}</h4>
                                        <v-btn 
                                            icon 
                                            small
                                            @click="goToNextFarm" 
                                            :disabled="currentFarmIndex === availableFarms.length - 1"
                                            class="ma-1 arrow-button"
                                        >
                                            <v-icon :color="currentFarmIndex === availableFarms.length - 1 ? 'grey' : '#FFFFFF'">mdi-chevron-right</v-icon>
                                        </v-btn>
                                    </div>
                                    <p class="mb-2 text-caption font-weight-medium">
                                        {{ $t('esgPlatform.surveyResponse.farmOf', {
                                            current: currentFarmIndex + 1,
                                            total: availableFarms.length
                                        }) }}
                                    </p>
                                </div>
                                <v-divider class="mt-4"></v-divider>
                            </div>


                            <div v-if="(isSeparateResponseQuestion || isSingleResponseQuestion) && currentFarmRejectionReason" class="rejection-block">
                                <p>{{ $t('esgPlatform.surveyResponse.submissionRehectedMsg') }}</p>
                                <p style="text-align: center">
                                    <span class="font-weight-bold">{{ $t('esgPlatform.surveyResponse.rejectionReason') }}: </span>{{ currentFarmRejectionReason }}
 
                                </p>
                            </div>

                            <!-- Question Inputs -->
                            <!-- Text Field -->
                            <v-text-field v-if="currentQuestion?.questionType === 'text-field'"
                                 v-model="textFieldModel" outlined dense
                                :rules="currentQuestion.isMandatory ? [v => !!v || $t('esgPlatform.surveyResponse.fieldRequired')] : []" />

                            <!-- Text Area -->
                            <v-textarea v-if="currentQuestion?.questionType === 'text-area'"
                                v-model="textFieldModel" outlined auto-grow rows="3"
                                :rules="currentQuestion.isMandatory ? [v => !!v || $t('esgPlatform.surveyResponse.fieldRequired')] : []" />

                            <!-- Numeric Field -->
                            <v-text-field v-if="currentQuestion?.questionType === 'numeric'"
                                 v-model.number="textFieldModel" type="number" outlined dense
                                :rules="currentQuestion.isMandatory ? [v => !!v || $t('esgPlatform.surveyResponse.fieldRequired')] : []" />

                            <!-- Radio Button (Optimized) -->
                            <v-radio-group v-if="currentQuestion?.questionType === 'radio-button'" v-model="radioModel"
                                :rules="currentQuestion.isMandatory ? [v => !!v || $t('esgPlatform.surveyResponse.fieldRequired')] : []"
                                @change="onRadioChange">
                                <v-radio class="radio-border"  v-for="option in currentQuestion?.options || []" :key="option._id"
                                    :label="option.label" :value="option._id" />
                            </v-radio-group>
                            <template v-if="currentQuestion?.questionType === 'radio-button' && selectedRadioOption">
                                <!-- Get Data Questions -->
                                <get-data-questions v-if="selectedRadioOption.isGetDataEnabled"
                                    :questions="selectedRadioOption.getDataQuestions" :responses="getDataResponseValue"
                                    :unit-type-list="unitTypeList" :unit-meta="unitMeta"
                                    @update-response="updateGetDataResponse" @file-change="handleGetDataFileChange"
                                    @remove-file="removeGetDataFile" @open-signature="openSignatureDialog"
                                    @remove-signature="removeSignature" />

                                <!-- Action Plans -->
                                <action-plans v-if="selectedRadioOption.isActionPlanEnabled"
                                    :plans="currentQuestion?.options?.filter(option => option._id === selectedRadioOption._id)[0]?.actionPlans"
                                    @file-change="handleActionPlanFileChange"
                                    
                                    @remove-file="handleActionPlanFileRemove" />


                                <!-- Recommendations -->
                                <v-card
                                    v-if="selectedRadioOption.isRecommendationEnabled && selectedRadioOption.recommendation"
                                    outlined class="pa-4 mt-4">
                                    <h4 class="mb-3">{{ $t('esgPlatform.surveyResponse.recommendations') }}</h4>
                                    <v-card-text class="recommendation-text">
                                        {{ selectedRadioOption.recommendation }}
                                    </v-card-text>
                                </v-card>
                            </template>

                            <!-- Checkbox -->
                            <template v-if="currentQuestion?.questionType === 'check-boxes'">
                                <div v-for="option in currentQuestion?.options || []" :key="option._id"
                                    class="option-container">
                                    <v-checkbox :input-value="isOptionSelected(option)" :label="option.label"
                                        @change="value => handleCheckboxChange(option, value)"
                                        :rules="currentQuestion.isMandatory ? [v => (Array.isArray(activeQuestionResponse) && activeQuestionResponse.length > 0) || $t('esgPlatform.surveyResponse.fieldRequired')] : []" />

                                    <!-- Get Data Questions for checkbox -->
                                    <get-data-questions v-if="isOptionSelected(option) && option.isGetDataEnabled"
                                        :questions="option.getDataQuestions"
                                        :responses="getCheckboxOptionGetDataResponses(option)"
                                        :unit-type-list="unitTypeList" :unit-meta="unitMeta"
                                        @update-response="updateGetDataResponse" @file-change="handleGetDataFileChange"
                                        @remove-file="removeGetDataFile" @open-signature="openSignatureDialog"
                                        @remove-signature="removeSignature"
                                        @checkbox-change="handleGetDataCheckboxChange"
                                        class="ml-4 mt-2 option-details" />

                                    <!-- Action Plans for checkbox -->
                                    <action-plans v-if="isOptionSelected(option) && option.isActionPlanEnabled"
                                        :plans="option.actionPlans" @file-change="handleActionPlanFileChange"
                                        @remove-file="handleActionPlanFileRemove" class="ml-4 mt-2 option-details" />

                                    <!-- Recommendations for checkbox -->
                                    <v-card v-if="isOptionSelected(option) && option.isRecommendationEnabled && option.recommendation"
                                        outlined class="pa-4 mt-4 option-details">
                                        <h4 class="mb-3">{{ $t('esgPlatform.surveyResponse.recommendations') }}</h4>
                                        <v-card-text class="recommendation-text">
                                            {{ option.recommendation }}
                                        </v-card-text>
                                    </v-card>
                                </div>
                            </template>

                            <!-- Date Field -->
                            <v-text-field v-if="currentQuestion?.questionType === 'date'"
                                v-model="textFieldModel" type="date"
                                :label="$t('esgPlatform.surveyResponse.enterYourAnswer')" outlined dense
                                :rules="currentQuestion.isMandatory ? [v => !!v || $t('esgPlatform.surveyResponse.fieldRequired')] : []" />

                            <!-- Digital Signature for Normal Questions -->
                            <template v-if="currentQuestion?.questionType === 'digital-signature'">
                                <div class="signature-section mt-4">
                                    <v-btn color="primary" @click="openSignatureDialog(currentQuestion)" class="ml-2">
                                        {{ $t('esgPlatform.surveyResponse.addSignature') }}
                                    </v-btn>
                                    <div v-if="signatureModel?.key || signatureModel?.s3key"
                                     class="mt-2">
                                        <img :src="signatureModel?.attachmentLink" :alt="$t('esgPlatform.digitalSignature')"
                                         style="max-width: 200px;" />
                                        <v-btn icon @click="removeSignature(currentQuestion)" class="ml-2">
                                            <v-icon>mdi-trash-can-outline</v-icon>
                                        </v-btn>
                                    </div>
                                </div>
                            </template>

                            <!-- Attach Files -->
                            <template v-if="currentQuestion.questionType === 'attach-files'">
                                <div class="file-upload-section mt-4">
                                    <v-row>
                                        <v-col cols="12" sm="6">
                                            <div class="upload-document">
                                                <div :class="['dropZone', { 'dropZone-over': dragging }]"
                                                    @dragenter="dragging = true" @dragleave="dragging = false"
                                                    @drop="handleQuestionDrop($event, currentQuestion)">
                                                    <div class="dropZone-info d-flex justify-center align-center"
                                                        @dragover.prevent>
                                                        <v-icon size="50" color="#0057BD">mdi-tray-arrow-up</v-icon>
                                                        <div class="dropZone-upload-limit-info">
                                                            <h2 class="ml-4 blue-color underline">
                                                                {{ $t('esgPlatform.surveyResponse.selectFile') }}
                                                            </h2>
                                                            <p class="gray-color">{{ $t('esgPlatform.surveyResponse.dragDrop') }}</p>
                                                        </div>
                                                    </div>
                                                    <input type="file" 
                                                        :multiple="getAttachFileSettings(currentQuestion).allowMultipleAttachments"
                                                        :accept="getFileAcceptTypes(getAttachFileSettings(currentQuestion).allowedFileTypes)"
                                                        @change="onQuestionFileChange($event, currentQuestion)" />
                                                </div>
                                            </div>
                                            <div :key="`attach-files-${currentQuestion._id}-${uploadKey}`" class="mt-6">
                                            <template v-if="attachFilesModel?.length">
                                                <div class="pdf-docs mt-10 d-flex flex-column flex-sm-row"
                                                    v-for="(doc, index) in attachFilesModel" :key="`file-${index}-${uploadKey}`">
                                                    <div class="docs-box mr-4 mb-4 mb-sm-0">
                                                        <div class="docs-preview">
                                                            <div class="img-item">
                                                                <div class="pdf-box">
                                                                    <h1 class="file-extension">
                                                                        {{ getFileExtension(doc) }}
                                                                    </h1>
                                                                </div>
                                                                <span class="file-name">
                                                                    {{ getFileName(doc) }}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div class="text-box mr-8"
                                                        v-if="canShowAttachFileComments(currentQuestion)">
                                                        <v-textarea v-model="doc.comment" rows="3.5" auto-grow
                                                            :placeholder="$t('esgPlatform.surveyResponse.comments')" outlined
                                                            :disabled="doc.isUploading"
                                                            @change="updateAttachFileComment(index, doc.comment, currentQuestion)" />
                                                    </div>
                                                    <div class="remove-file-btn">
                                                        <v-btn icon small color="error" @click="removeAttachFile(index, currentQuestion)">
                                                            <v-icon>mdi-trash-can-outline</v-icon>
                                                        </v-btn>
                                                    </div>
                                                </div>
                                            </template>
                                            </div>
                                        </v-col>
                                    </v-row>
                                </div>
                            </template>

                            <!-- Get-Data Questions -->
                            <template
                                v-if="currentQuestion.questionType === 'get-data' && currentQuestion?.getDataQuestions?.length > 0">
                                <div class="mt-4">
                                    <div v-for="getDataQuestion in currentQuestion.getDataQuestions"
                                        :key="getDataQuestion._id" class="mt-2">
                                        <div class="d-flex align-center">
                                            <p class="text-subtitle-2">{{ getDataQuestion.title }}</p>

                                            <v-tooltip v-if="getDataQuestion.helpText && getDataQuestion.helpEnabled"
                                                right color="00BD73" max-width="500">
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-icon class="icon-with-background pl-2 mb-4" style="color:#0EBF67"
                                                        v-bind="attrs" v-on="on">
                                                        mdi-alert-circle
                                                    </v-icon>
                                                </template>
                                                <p class="text-caption mb-2">
                                                    {{ getDataQuestion.helpText }}
                                                </p>
                                            </v-tooltip>
                                        </div>

                                        <!-- File Upload for Get-Data -->
                                        <template v-if="getDataQuestion.dataType === 'attach-file'">
                                            <div class="file-upload-section">
                                                <v-row>
                                                    <v-col cols="12" sm="6" class="mb-10">
                                                        <div class="upload-document">
                                                            <div :class="['dropZone', { 'dropZone-over': dragging }]"
                                                                @dragenter="dragging = true"
                                                                @dragleave="dragging = false"
                                                                @drop="handleGetDataDrop($event, getDataQuestion)">
                                                                <div class="dropZone-info d-flex justify-center align-center"
                                                                    @dragover.prevent>
                                                                    <v-icon size="50"
                                                                        color="#0057BD">mdi-tray-arrow-up</v-icon>
                                                                    <div class="dropZone-upload-limit-info">
                                                                        <h2 class=" ml-4 blue-color underline">
                                                                            {{
                                                                                $t('esgPlatform.surveyResponse.selectFile')
                                                                            }}</h2>
                                                                        <p class="gray-color">
                                                                            {{ $t('esgPlatform.surveyResponse.dragDrop')
                                                                            }}</p>
                                                                    </div>
                                                                </div>
                                                                <input type="file" multiple
                                                                    :accept="getFileAcceptTypes(getDataQuestion.allowedTypes)"
                                                                    @change="onGetDataFileChange($event, getDataQuestion)" />
                                                            </div>
                                                        </div>
                                                        <div class="pdf-docs mt-10 d-flex flex-column flex-sm-row"
                                                            v-for="(doc, index) in currentQuestionResponse(getDataQuestion)"
                                                            :key="index">
                                                            <div class="docs-box mr-4 mb-4 mb-sm-0">
                                                                <div class="docs-preview">
                                                                    <div class="img-item">
                                                                        <div class="pdf-box">
                                                                            <v-icon class="delete-icon"
                                                                                @click="removeGetDataFile(index, getDataQuestion)">
                                                                                mdi-trash-can-outline
                                                                            </v-icon>
                                                                            <h1 class="file-extension">
                                                                                {{ getFileExtension(doc) }}
                                                                            </h1>
                                                                        </div>
                                                                        <span class="file-name">{{ getFileName(doc)}}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div v-if="getDataQuestion.isMandatory && fileErrors[getDataQuestion._id]"
                                                            class="red--text text-caption mt-2">
                                                            {{ fileErrors[getDataQuestion._id] }}
                                                        </div>
                                                    </v-col>
                                                </v-row>
                                            </div>
                                        </template>

                                        <!-- Digital Signature for Get-Data Questions -->
                                        <template v-if="getDataQuestion.dataType === 'digital-signature'">
                                            <div class="signature-section">
                                                <v-btn color="primary" @click="openSignatureDialog(getDataQuestion)"
                                                    class="ml-2">
                                                    {{ $t('esgPlatform.surveyResponse.addSignature') }}
                                                </v-btn>
                                                <div v-if="currentQuestionResponse(getDataQuestion) && (currentQuestionResponse(getDataQuestion)?.key || currentQuestionResponse(getDataQuestion)?.s3key)"
                                                    class="mt-2">
                                                    <img :src="currentQuestionResponse(getDataQuestion).attachmentLink"
                                                        :alt="$t('esgPlatform.digitalSignature')" style="max-width: 200px;" />
                                                    <v-btn icon @click="removeSignature(getDataQuestion)" class="ml-2">
                                                        <v-icon>mdi-trash-can-outline</v-icon>
                                                    </v-btn>
                                                </div>

                                                <div v-if="getDataQuestion.isMandatory && signatureErrors[getDataQuestion._id]"
                                                    class="red--text text-caption mt-2">
                                                    {{ signatureErrors[getDataQuestion._id] }}
                                                </div>
                                            </div>
                                        </template>

                                        <!-- Measurement Unit -->
                                        <template v-if="getDataQuestion.dataType === 'measurement-unit'">
                                            <div class="d-flex align-center">
                                                <v-text-field :placeholder="$t('esgPlatform.dashboard.enterHere')"
                                                    :value="getMeasurementValue(getDataQuestion)"
                                                    @input="val => updateMeasurementResponse(getDataQuestion, val, getMeasurementUnit(getDataQuestion))"
                                                    type="number" outlined dense class="mr-2"
                                                    :rules="getDataQuestion.isMandatory ? [v => !!v || $t('esgPlatform.surveyResponse.fieldRequired')] : []"
                                                    style="max-width:300px" />

                                                <v-select
                                                    :disabled="!getDataQuestion.additionalSettings?.allowUnitChange"
                                                    :items="Object.keys(unitTypeList)"
                                                    :value="getMeasurementUnitType(getDataQuestion)"
                                                    @change="type => onUnitTypeChange(getDataQuestion, type)"
                                                    :label="$t('esgPlatform.surveyResponse.unitType')" outlined dense class="mr-2"
                                                    style="max-width: 120px" />

                                                <v-select
                                                    :disabled="!getDataQuestion.additionalSettings?.allowUnitChange"
                                                    :items="unitTypeList[getMeasurementUnitType(getDataQuestion)] || []"
                                                    :value="getMeasurementUnit(getDataQuestion)"
                                                    @change="unit => onUnitChange(getDataQuestion, unit)" :label="$t('esgPlatform.surveyResponse.unit')"
                                                    outlined dense style="max-width: 100px" />
                                            </div>
                                        </template>

                                        <!-- Text Area -->
                                        <v-textarea v-if="getDataQuestion.dataType === 'text-area'"
                                            :placeholder="$t('esgPlatform.dashboard.enterHere')"
                                            :value="currentQuestionResponse(getDataQuestion)" @input="val => updateGetDataResponse({
                                                questionId: getDataQuestion._id,
                                                value: val,
                                                dataType: getDataQuestion.dataType
                                            })" outlined auto-grow rows="2"
                                            :rules="getDataQuestion.isMandatory ? [v => !!v || $t('esgPlatform.surveyResponse.fieldRequired')] : []" />

                                        <!-- Text Field -->
                                        <v-text-field :placeholder="$t('esgPlatform.dashboard.enterHere')"
                                            v-if="getDataQuestion.dataType === 'text-field'"
                                            :value="currentQuestionResponse(getDataQuestion)" @input="val => updateGetDataResponse({
                                                questionId: getDataQuestion._id,
                                                value: val,
                                                dataType: getDataQuestion.dataType
                                            })" outlined dense
                                            :rules="getDataQuestion.isMandatory ? [v => !!v || $t('esgPlatform.surveyResponse.fieldRequired')] : []" />

                                        <!-- Numeric Field -->
                                        <v-text-field v-if="getDataQuestion.dataType === 'numeric'" outlined dense
                                            :value="currentQuestionResponse(getDataQuestion)" @input="val => updateGetDataResponse({
                                                questionId: getDataQuestion._id,
                                                value: val,
                                                dataType: getDataQuestion.dataType
                                            })" type="number"
                                            :rules="getDataQuestion.isMandatory ? [v => !!v || $t('esgPlatform.surveyResponse.fieldRequired')] : []" />

                                        <!-- Date Field -->
                                        <v-text-field v-if="getDataQuestion.dataType === 'date'"
                                            :value="currentQuestionResponse(getDataQuestion)" @input="val => updateGetDataResponse({
                                                questionId: getDataQuestion._id,
                                                value: val,
                                                dataType: getDataQuestion.dataType
                                            })" type="date" outlined dense
                                            :rules="getDataQuestion.isMandatory ? [v => !!v || $t('esgPlatform.surveyResponse.fieldRequired')] : []" />

                                        <!-- Radio Field -->
                                        <v-radio-group v-if="getDataQuestion.dataType === 'radio-button'"
                                            :value="currentQuestionResponse(getDataQuestion)" @change="val => updateGetDataResponse({
                                                questionId: getDataQuestion._id,
                                                value: val,
                                                dataType: getDataQuestion.dataType
                                            })"
                                            :rules="getDataQuestion.isMandatory ? [v => !!v || $t('esgPlatform.surveyResponse.fieldRequired')] : []">
                                            <v-radio
                                                v-for="(option, index) in getDataQuestion.options || getDataQuestion.optionValues"
                                                :key="option.value || option._id || index"
                                                :label="option.label || option" :value="option.value || option" />
                                        </v-radio-group>

                                        <!-- checkbox field -->
                                        <div v-if="getDataQuestion.dataType === 'check-boxes'">
                                            <v-checkbox
                                                v-for="(option, index) in getDataQuestion.options || getDataQuestion.optionValues"
                                                :key="option.value || option._id || index"
                                                :label="option.label || option" :value="option.value || option"
                                                :input-value="currentQuestionResponse(getDataQuestion) || []" @change="val => {
                                                    let arr = currentQuestionResponse(getDataQuestion) ? [...currentQuestionResponse(getDataQuestion)] : [];
                                                    if (val) {
                                                        if (!arr.includes(option.value || option)) arr.push(option.value || option);
                                                    } else {
                                                        arr = arr.filter(v => v !== (option.value || option));
                                                    }
                                                    updateGetDataResponse({
                                                        questionId: getDataQuestion._id,
                                                        value: arr,
                                                        dataType: getDataQuestion.dataType
                                                    });
                                                }" />
                                        </div>

                                    </div>
                                </div>
                            </template>

                            </v-form>
                        </v-col>
                    </v-row>

                    <!-- Bottom Navigation -->
                    <v-row class="mt-6 justify-center flex-column flex-sm-row">
                        <v-btn v-if="showSubmitActionButton" color="primary" class="ma-2 navigation-btn" @click="goToSurveyOverview">
                            {{ $t('esgPlatform.esgReport.back') }}
                        </v-btn>
                        <v-btn v-if="showSubmitActionButton" color="primary" dark class="ma-2 navigation-btn" @click="openConfirmBox = true; 
                        message = $t('esgPlatform.confirmSurveyResponse'); confirmAction = submitEntireSurvey" :disabled="isSubmitDisabled">
                            {{ showSubmitAgainButton ? $t('esgPlatform.surveyResponse.submitAgain') : $t('esgPlatform.surveyResponse.submit') }}
                        </v-btn>
                    </v-row>
                </div>
                    </v-card>
                </v-col>
                <v-col v-if="hasRepeatSubmissions" cols="12" md="4" class="repeat-sidebar-col">
                    <div class="repeat-sidebar">
                        <h3 class="repeat-title">{{ $t('esgPlatform.surveyResponse.repeatSubmission') }}</h3>
                        <div class="repeat-list">
                            <button
                                v-for="item in repeatSidebarItems"
                                :key="item.key"
                                type="button"
                                class="repeat-item"
                                :class="{
                                    'repeat-item--selected': item.index === selectedDateIndex,
                                    'repeat-item--active': item.isActiveWindow && item.index === selectedDateIndex,
                                    'repeat-item--disabled': item.isUpcoming
                                }"
                                :disabled="item.isUpcoming"
                                @click="selectRepeatDate(item.index)"
                            >
                                <div class="repeat-radio">
                                    <div v-if="item.index === selectedDateIndex" class="repeat-radio-inner"></div>
                                </div>
                                <div class="repeat-item-content">
                                    <div class="repeat-item-label">
                                        {{ $t('esgPlatform.surveyResponse.submissionOf', {
                                            current: item.displayIndex,
                                            total: repeatCount
                                        }) }}
                                    </div>
                                    <div class="repeat-item-meta">
                                        <!-- Rejected: date + reason -->
                                        <template v-if="item.isRejected">
                                            <span>{{ formatWithMoment(item.repetitionDate) }}</span>
                                            <span class="repeat-rejected ml-2">{{ $t('esgPlatform.surveyResponse.responseRejected') }}</span>
                                        </template>

                                        <!-- Has response (submitted) -->
                                        <span v-else-if="item.hasResponse">
                                            {{ formatWithMoment(item.repetitionDate) }}
                                        </span>

                                        <!-- Active window: not yet submitted -->
                                        <span v-else-if="item.isActiveWindow">
                                            {{ formatWithMoment(item.repetitionDate) }} - {{ $t('esgPlatform.surveyResponse.notYetSubmitted') }}
                                        </span>

                                        <!-- Next after active: show date only -->
                                        <span v-else-if="item.isNextVisible">
                                            {{ formatWithMoment(item.repetitionDate) }}
                                        </span>

                                        <!-- Further future: Upcoming -->
                                        <span v-else-if="item.isHiddenUpcoming">{{ $t('esgPlatform.surveyResponse.upcoming') }}</span>

                                        <!-- Past date, no response -->
                                        <span v-else-if="item.isPast">
                                            {{ formatWithMoment(item.repetitionDate) }} - {{ $t('esgPlatform.surveyResponse.notSubmitted') }}
                                        </span>
                                    </div>
                                </div>
                            </button>
                        </div>
                    </div>
                </v-col>
            </v-row>

        </v-container>
        <ConfirmBox :dialog="openConfirmBox" @cancel:action="openConfirmBox = false" @continue:action="handleConfirm"
            :message="message" :confirmBtnText="$t('confirm')" :cancelBtnText="$t('cancel')" />
        <SignatureDialog v-if="showSignatureDialog" :value="showSignatureDialog" @input="showSignatureDialog = $event"
            :options="signatureOptions" @signature="handleSignature" />
    </div>
</template>

<script>
import ConfirmBox from '../../standards/components/confirmationDialog.vue';
import loadingMixin from '@/mixins/LoadingMixin';
import EsgService from '@/_services/EsgService';
import S3UploadService from '@/_services/S3UploadService';
import SignatureDialog from '../components/SignatureModal.vue';
import GetDataQuestions from './GetDataQuestions.vue';
import ActionPlans from './ActionPlans.vue';
import moment from 'moment';
import { formatLocaleDate } from '@/utils/localeFormat';

export default {
    name: 'SurveyOverview',
    components: { ConfirmBox, SignatureDialog, GetDataQuestions, ActionPlans },
    mixins: [loadingMixin],

    data() {
        return {
            isLoading: false,
            openConfirmBox: false,
            message: '',
            confirmAction: null,
            goalTitle: '',
            breadcrumbs: [
                { text: this.$t('esgPlatform.esgPlatform'), link: true, exact: true, disabled: true },
                { text: this.$t('dashboard'), disabled: false, to: { name: 'eudrDashboard' } },
            ],
            currentQuestionIndex: 0,
            rawQuestions: [],
            esgProtocolId: null,
            esgIssueId: null,
            currentGoalId: null,
            currentAssessmentId: null,
            dragging: false,
            goalId: this.$route.params.id2,
            surveyId: this.$route.params.id1,
            showSignatureDialog: false,
            currentSignatureQuestion: null,
            esgSubOrganization: null,
            signatureOptions: {},
            fileErrors: {},
            signatureErrors: {},
            formValid: true,
            unitTypeList: {
                length: ["mm", "cm", "m", "km", "mi", "ft"],
                area: ["sq. m", "sq. km", "sq. ft", "yard", "hectares"],
                volume: ["cu. mm", "cu. m", "litres"]
            },
            unitMeta: {
                mm: { id: 'mm', name: 'Millimeter', abbr: 'mm', factor: 1, category: 'length' },
                cm: { id: 'cm', name: 'Centimeter', abbr: 'cm', factor: 10, category: 'length' },
                m: { id: 'm', name: 'Meter', abbr: 'm', factor: 1000, category: 'length' },
                km: { id: 'km', name: 'Kilometer', abbr: 'km', factor: 1000000, category: 'length' },
                mi: { id: 'mi', name: 'Mile', abbr: 'mi', factor: 1609344, category: 'length' },
                ft: { id: 'ft', name: 'Foot', abbr: 'ft', factor: 304.8, category: 'length' },
                sqm: { id: 'sqm', name: 'Square Meter', abbr: 'sqm', factor: 1, category: 'area' },
                sqkm: { id: 'sqkm', name: 'Square Kilometer', abbr: 'sqkm', factor: 1000000, category: 'area' },
                sqft: { id: 'sqft', name: 'Square Foot', abbr: 'sqft', factor: 929.03, category: 'area' },
                yard: { id: 'yard', name: 'Yard', abbr: 'yard', factor: 0.836127, category: 'area' },
                hectares: { id: 'hectares', name: 'Hectare', abbr: 'ha', factor: 10000, category: 'area' },
                cu_mm: { id: 'cu_mm', name: 'Cubic Millimeter', abbr: 'cu_mm', factor: 1, category: 'volume' },
                cu_m: { id: 'cu_m', name: 'Cubic Meter', abbr: 'cu_m', factor: 1000000, category: 'volume' },
                litres: { id: 'litres', name: 'Litre', abbr: 'l', factor: 1000, category: 'volume' },
            },
            currentFarmId: null, 
            farmResponses: {},
            selectedDateIndex: 0,
            repeatDraftResponses: {},
            uploadKey: 0,
        };
    },

    computed: {
        currentFarm() {
            if (!this.currentFarmId || !this.availableFarms.length) return null;
            return this.availableFarms.find(farm => farm._id === this.currentFarmId);
        },
        repetitionDates() {
            if (!this.currentQuestion) return [];
            if (Array.isArray(this.currentQuestion.repetitionDates)) {
                return this.currentQuestion.repetitionDates;
            }
            if (Array.isArray(this.currentQuestion.repeat?.selectedDates)) {
                return this.currentQuestion.repeat.selectedDates;
            }
            return [];
        },
        hasRepeatSubmissions() {
            return this.repetitionDates.length > 0;
        },
        repeatCount() {
            if (!this.currentQuestion) return this.repetitionDates.length;
            return this.currentQuestion.repetitionCount || this.repetitionDates.length;
        },
        nextUpcomingIndex() {
            if (!this.hasRepeatSubmissions) return -1;
            let nextIndex = -1;
            let nextDate = null;
            this.repetitionDates.forEach((date, index) => {
                if (!this.isRepeatUpcoming(index)) return;
                const normalized = this.normalizeRepeatDate(date);
                if (!normalized) return;
                if (!nextDate || normalized.isBefore(nextDate)) {
                    nextDate = normalized;
                    nextIndex = index;
                }
            });
            return nextIndex;
        },
        selectedRepetitionDate() {
            return this.repetitionDates[this.selectedDateIndex] || null;
        },
        activeRepeatResponse() {
            if (!this.hasRepeatSubmissions || !this.currentQuestion) return null;
            const responses = Array.isArray(this.currentQuestion._repeatResponses)
                ? this.currentQuestion._repeatResponses
                : [];
            return responses[this.selectedDateIndex] || null;
        },
        activeRepeatResponseSubmittedOn() {
            return this.getRepeatResponseSubmittedOn(this.activeRepeatResponse);
        },
        displaySubmittedOn() {
            if (this.hasRepeatSubmissions) {
                return this.activeRepeatResponseSubmittedOn;
            }
            return this.currentQuestion?.submittedOn || null;
        },
        repeatPlaceholderText() {
            if (!this.hasRepeatSubmissions) return '';
            if (this.activeRepeatResponse) return '';
            if (this.isActiveRepeatWindow) return '';
            return this.isRepeatUpcoming(this.selectedDateIndex)
                ? this.$t('esgPlatform.surveyResponse.upcoming')
                : this.$t('esgPlatform.surveyResponse.notYetSubmitted');
        },
        repeatSidebarItems() {
            if (!this.hasRepeatSubmissions) return [];
            const responses = Array.isArray(this.currentQuestion?._repeatResponses)
                ? this.currentQuestion._repeatResponses
                : [];

            // Find active window index (today falls in this window)
            const activeIndex = this.repetitionDates.findIndex((_, i) => this.isRepeatWindowOpen(i));
            // Next after active = activeIndex + 1, show date only, NOT disabled
            const nextVisibleIndex = activeIndex >= 0 ? activeIndex + 1 : -1;

            return this.repetitionDates.map((date, index) => {
                const response = responses[index] || null;
                const hasResponse = !!response;
                const rejectionReason = response?.rejectionReason ?? null;
                const isActiveWindow = this.isRepeatWindowOpen(index);

                // isUpcoming: future date BUT next visible should NOT be disabled
                const rawUpcoming = this.isRepeatUpcoming(index);
                const isNextVisible = index === nextVisibleIndex;
                const isUpcoming = rawUpcoming && !isNextVisible; // next visible is NOT disabled
                const isHiddenUpcoming = rawUpcoming && !isNextVisible; // further future = "Upcoming"
                const isPast = !isActiveWindow && !rawUpcoming && !hasResponse; // passed with no response

                return {
                    key: `${date || 'repeat'}-${index}`,
                    index,
                    displayIndex: index + 1,
                    repetitionDate: date,
                    response,
                    hasResponse,
                    isRejected: !!rejectionReason,
                    rejectionReason,
                    isActiveWindow,
                    isUpcoming,       // controls :disabled
                    isNextVisible,    // show date only
                    isHiddenUpcoming, // show "Upcoming"
                    isPast,           // show "Not submitted"
                };
            });
        },
        isActiveRepeatWindow() {
            if (!this.hasRepeatSubmissions) return true;
            return this.isRepeatWindowOpen(this.selectedDateIndex);
        },
        isRepeatReadOnly() {
            return this.hasRepeatSubmissions && !this.isActiveRepeatWindow;
        },
        isSubmitDisabled() {
            if (this.isSeparateResponseQuestion || this.isSingleResponseQuestion) return true;
            if (!this.hasRepeatSubmissions) return false;
            return !this.isActiveRepeatWindow;
        },
         currentFarmRejectionReason() {
            if (!this.currentQuestion) return null;
            const responseSource = this.currentQuestion._originalResponse || this.currentQuestion.response;
            if (!responseSource) return null;
            const responses = Array.isArray(responseSource) 
                ? responseSource 
                : [responseSource];

            // For single response farmer questions - check first (no currentFarmId needed)
            if (this.isSingleResponseQuestion) {
                const response = responses.find(resp => resp && resp.rejectionReason);
                return response?.rejectionReason || null;
            }
            // For separate response farmer questions - need currentFarmId
            if (this.isSeparateResponseQuestion && this.currentFarmId) {
                const farmResponse = responses.find(resp => 
                    resp && resp.farmIds && resp.farmIds.includes(this.currentFarmId)
                );
                return farmResponse?.rejectionReason || null;
            }
            // For get-data questions
            if (this.currentQuestion.questionType === 'get-data' && this.currentFarmId) {
                const farmResponse = responses.find(resp =>
                    resp && resp.farmIds && resp.farmIds.includes(this.currentFarmId)
                );
                return farmResponse?.rejectionReason || null;
            }
            
            return null;
        },
        submittedResponse() {
            if (!this.currentQuestion) return null;
            if (this.hasRepeatSubmissions) {
                return this.activeRepeatResponse
                    ? (this.extractAssessmentResponse(this.activeRepeatResponse) || null)
                    : null;
            }
            if (typeof this.currentQuestion._originalResponse !== 'undefined') {
                return this.currentQuestion._originalResponse;
            }
            return null;
        },
        submittedRejectionReason() {
            if (!this.currentQuestion) return null;
            if (this.hasRepeatSubmissions) {
                const repeatReason = this.activeRepeatResponse?.rejectionReason;
                if (repeatReason !== null && repeatReason !== undefined && repeatReason !== '') {
                    return repeatReason;
                }
            }
            const reason = this.currentQuestion.rejectionReason;
            if (reason !== null && reason !== undefined && reason !== '') {
                return reason;
            }
            if (this.currentFarmRejectionReason !== null && this.currentFarmRejectionReason !== undefined && this.currentFarmRejectionReason !== '') {
                return this.currentFarmRejectionReason;
            }
            return null;
        },
        showSubmitButton() {
            const response = this.submittedResponse;
            const rejectionReason = this.submittedRejectionReason;
            const hasValidResponse = response === null || (Array.isArray(response) && response.length === 0);
            return hasValidResponse && rejectionReason === null;
        },
        showSubmitAgainButton() {
            const response = this.submittedResponse;
            const rejectionReason = this.submittedRejectionReason;
            return response !== null && rejectionReason !== null;
        },
        showSubmitActionButton() {
            return this.showSubmitButton || this.showSubmitAgainButton;
        },
        activeQuestionResponse: {
            get() {
                if (!this.currentQuestion) return null;
                if (!this.hasRepeatSubmissions) {
                    return this.currentQuestion.response;
                }
                const draft = this.ensureRepeatDraft(this.currentQuestion, this.selectedDateIndex);
                return draft ? draft.response : this.getEmptyQuestionResponse(this.currentQuestion);
            },
            set(value) {
                if (!this.currentQuestion) return;
                if (!this.hasRepeatSubmissions) {
                    this.$set(this.currentQuestion, 'response', value);
                    return;
                }
                const draft = this.ensureRepeatDraft(this.currentQuestion, this.selectedDateIndex);
                if (!draft) return;
                this.$set(draft, 'response', value);
                this.syncQuestionViewStateFromResponse(this.currentQuestion, value);
            }
        },
        activeUploadedFiles: {
            get() {
                const uploadKey = this.uploadKey;
                void uploadKey;
                if (!this.currentQuestion) return [];
                if (!this.hasRepeatSubmissions) {
                    return this.getAttachFilesForQuestion(this.currentQuestion);
                }
                const draft = this.ensureRepeatDraft(this.currentQuestion, this.selectedDateIndex);
                return draft?.uploadedFiles || [];
            },
            set(value) {
                if (!this.currentQuestion) return;
                const uploadedFiles = this.normalizeAttachFiles(value || []);
                if (!this.hasRepeatSubmissions) {
                    this.$set(this.currentQuestion, 'uploadedFiles', uploadedFiles);
                    if (this.currentQuestion.questionType === 'attach-files') {
                        this.$set(this.currentQuestion, 'response', {
                            id: this.currentQuestion._id,
                            value: this.mapAttachFilesToResponseValue(uploadedFiles)
                        });
                    }
                    return;
                }
                const draft = this.ensureRepeatDraft(this.currentQuestion, this.selectedDateIndex);
                if (!draft) return;
                this.$set(draft, 'uploadedFiles', uploadedFiles);
                if (this.currentQuestion.questionType === 'attach-files') {
                    this.$set(draft, 'response', {
                        value: this.mapAttachFilesToResponseValue(uploadedFiles)
                    });
                }
            }
        },
        activeSignature: {
            get() {
                if (!this.currentQuestion) return null;
                if (!this.hasRepeatSubmissions) {
                    return this.currentQuestion.signature || null;
                }
                const draft = this.ensureRepeatDraft(this.currentQuestion, this.selectedDateIndex);
                return draft?.signature || null;
            },
            set(value) {
                if (!this.currentQuestion) return;
                if (!this.hasRepeatSubmissions) {
                    const signature = value || null;
                    this.$set(this.currentQuestion, 'signature', signature);
                    if (this.currentQuestion.questionType === 'digital-signature') {
                        this.$set(this.currentQuestion, 'response', {
                            value: signature ? {
                                attachmentLink: signature.attachmentLink,
                                key: signature.s3key || signature.key
                            } : null
                        });
                    }
                    return;
                }
                const draft = this.ensureRepeatDraft(this.currentQuestion, this.selectedDateIndex);
                if (!draft) return;
                const signature = value || null;
                this.$set(draft, 'signature', signature);
                if (this.currentQuestion.questionType === 'digital-signature') {
                    this.$set(draft, 'response', {
                        value: signature ? {
                            attachmentLink: signature.attachmentLink,
                            key: signature.s3key || signature.key
                        } : null
                    });
                }
            }
        },
        textFieldModel: {
            get() {
                if (!this.currentQuestion) return '';
                if (this.isSeparateResponseQuestion && this.currentFarmId) {
                    const farmResponse = this.currentFarmResponse;
                    if (farmResponse && farmResponse.value !== undefined) {
                        return farmResponse.value;
                    }
                    return '';
                }
                const response = this.activeQuestionResponse;
                if (response && typeof response === 'object' && Object.prototype.hasOwnProperty.call(response, 'value')) {
                    return response.value ?? '';
                }
                return response ?? '';
            },
            set(value) {
                if (!this.currentQuestion) return;
                
                // For separate response questions, store per-farm
                if (this.isSeparateResponseQuestion && this.currentFarmId) {
                    const farmId = this.currentFarmId;
                    const questionId = this.currentQuestion._id;
                    if (!this.farmResponses[farmId]) {
                    this.$set(this.farmResponses, farmId, {});
                    }
                    this.$set(this.farmResponses[farmId], questionId, { value: value, farmIds: [farmId] });
                    return;
                }
                this.activeQuestionResponse = value;
            }
        },

        attachFilesModel: {
            get() {
                const uploadKey = this.uploadKey;
                void uploadKey;
                if (!this.currentQuestion) return [];
                if (this.isSeparateResponseQuestion && this.currentFarmId) {
                    return this.getAttachFilesForQuestion(this.currentQuestion);
                }
                
                return this.activeUploadedFiles || [];
            },
            set(value) {
                if (!this.currentQuestion) return;
                const uploadedFiles = this.normalizeAttachFiles(value || []);
                
                if (this.isSeparateResponseQuestion && this.currentFarmId) {
                    const farmId = this.currentFarmId;
                    const questionId = this.currentQuestion._id;
                    
                    if (!this.farmResponses[farmId]) {
                        this.$set(this.farmResponses, farmId, {});
                    }
                    
                    this.$set(this.farmResponses[farmId], questionId, { 
                        uploadedFiles,
                        value: this.mapAttachFilesToResponseValue(uploadedFiles),
                        farmIds: [farmId]
                    });
                    return;
                }
                
                this.activeUploadedFiles = uploadedFiles;
            }
        },

        signatureModel: {
            get() {
                if (!this.currentQuestion) return null;
                
                if (this.isSeparateResponseQuestion && this.currentFarmId) {
                    const farmResponse = this.currentFarmResponse;
                    if (farmResponse && farmResponse.value) {
                        return farmResponse.value;
                    }
                    const farmId = this.currentFarmId;
                    const questionId = this.currentQuestion._id;
                    if (this.farmResponses[farmId] && this.farmResponses[farmId][questionId]) {
                        return this.farmResponses[farmId][questionId].value;
                    }
                    return null;
                }

                return this.activeSignature;
            },
            set(value) {
                if (!this.currentQuestion) return;
                
                if (this.isSeparateResponseQuestion && this.currentFarmId) {
                    const farmId = this.currentFarmId;
                    const questionId = this.currentQuestion._id;
                    
                    if (!this.farmResponses[farmId]) {
                        this.$set(this.farmResponses, farmId, {});
                    }
                    
                    this.$set(this.farmResponses[farmId], questionId, { 
                        value: value,
                        farmIds: [farmId]
                    });
                    return;
                }

                this.activeSignature = value;
            }
        },
        // Transform the API response into a flat questions array
        questions() {
            if (!this.rawQuestions) return [];

            return this.rawQuestions.reduce((allQuestions, heading) => {
                const validQuestions = (heading.assessmentQuestions || [])
                    .filter(q => !q.deletedAt)
                    .map(q => ({
                        ...q,
                        questionHeading: {
                            _id: heading._id,
                            title: heading.title,
                            order: heading.order
                        }
                    }));
                return [...allQuestions, ...validQuestions];
            }, []).sort((a, b) => {
                // First sort by heading order
                const headingOrderDiff = a.questionHeading.order - b.questionHeading.order;
                if (headingOrderDiff !== 0) return headingOrderDiff;
                // Then sort by question order within heading
                return a.order - b.order;
            });
        },

        // Current heading's questions
        currentHeadingQuestions() {
            if (!this.currentQuestion || !this.rawQuestions) return [];
            const currentHeading = this.rawQuestions.find(h => h._id === this.currentQuestion.questionHeading?._id);
            return (currentHeading?.assessmentQuestions || [])
                .filter(q => !q.deletedAt)
                .sort((a, b) => a.order - b.order);
        },

        currentQuestion() {
            return this.questions[this.currentQuestionIndex] || null;
        },

        currentQuestionResponse: {
            get() {
                return (getDataQuestion) => {
                    console.log(' get current question response', this.currentQuestion, getDataQuestion);
                    const questionResponse = this.activeQuestionResponse;
                    
                    // Handle different question types and response structures
                    if (this.currentQuestion?.questionType === 'radio-button') {
                        // For radio buttons, look in getDataResponses
                        const getDataResponses = questionResponse?.getDataResponses || [];
                        const response = getDataResponses.find(resp => resp?.id === getDataQuestion._id);
                        return response ? response.value : '';
                    } else if (this.currentQuestion?.questionType === 'check-boxes') {
                        // For checkboxes, look in each option's getDataResponses
                        if (Array.isArray(questionResponse)) {
                            for (const optionResponse of questionResponse) {
                                if (optionResponse?.getDataResponses) {
                                    const response = optionResponse.getDataResponses.find(resp => resp?.id === getDataQuestion._id);
                                    if (response) {
                                        return response.value;
                                    }
                                }
                            }
                        }
                        return '';
                    } else {
                        // For direct getData questions, use the original logic
                        const responses = Array.isArray(questionResponse)
                            ? questionResponse
                            : [];
                        
                        // For separate response questions, filter by current farm
                        if (this.isSeparateResponseQuestion && this.currentFarmId) {
                            const farmResponse = responses.find(
                                resp => resp?.id === getDataQuestion._id && resp?.farmIds?.includes(this.currentFarmId)
                            );
                            return farmResponse ? farmResponse.value : '';
                        }
                        
                        const response = responses.find(
                            resp => resp?.id === getDataQuestion._id
                        );
                        return response ? response.value : '';
                    }
                };
            },
            set() {
                return (getDataQuestion, newValue) => {
                    let questionResponse = this.activeQuestionResponse;
                    // Ensure response is an array before using .findIndex
                    if (!Array.isArray(questionResponse)) {
                        questionResponse = [];
                        this.activeQuestionResponse = questionResponse;
                    }
                    const responseIndex = questionResponse.findIndex(
                        resp => resp.id === getDataQuestion._id
                    );
                    if (responseIndex > -1) {
                        // Update existing response
                        this.$set(questionResponse[responseIndex], 'value', newValue);
                    } else {
                        // Create new response
                        questionResponse.push({
                            id: getDataQuestion._id,
                            title: getDataQuestion.title,
                            dataType: getDataQuestion.dataType,
                            value: newValue
                        });
                    }
                };
            }
        },

        selectedActionPlans() {
            if (!this.currentQuestion ||
                this.currentQuestion.questionType !== 'radio-button' ||
                !this.activeQuestionResponse?.value) {
                return [];
            }
            const selectedOption = this.currentQuestion.options.find(opt =>
                opt._id === this.activeQuestionResponse?.value
            );
            return selectedOption?.actionPlans || [];
        },

        currentQuestionNumber() {
            if (!this.currentQuestion || !this.currentHeadingQuestions) return 1;
            return this.currentHeadingQuestions.findIndex(q => q._id === this.currentQuestion._id) + 1;
        },
        selectedRadioOption() {
            if (!this.currentQuestion || this.currentQuestion.questionType !== 'radio-button') return null;
            return this.currentQuestion.options.find(opt => opt._id == this.activeQuestionResponse?.value);
        },
        getDataResponseValue: {
            get() {
                if (!this.currentQuestion) return {};
                const questionType = this.currentQuestion.questionType;
                const questionResponse = this.activeQuestionResponse;

                if (questionType === 'get-data' && this.isSeparateResponseQuestion && this.currentFarmId) {
                    const farmId = this.currentFarmId;
                    const questionId = this.currentQuestion._id;
                    if (this.farmResponses[farmId]?.[questionId]) {
                        const storedResponse = this.farmResponses[farmId][questionId];
                        if (Array.isArray(storedResponse)) {
                            // Convert array of responses to object format
                            return storedResponse.reduce((acc, item) => {
                                acc[item.id] = item.value;
                                return acc;
                            }, {});
                        }
                    }

                    if (Array.isArray(questionResponse)) {
                        const farmSpecificResponses = questionResponse.filter(resp =>
                            resp.farmIds?.includes(farmId)
                        );

                        return farmSpecificResponses.reduce((acc, item) => {
                            acc[item.id] = item.value;
                            return acc;
                        }, {});
                    }
                    
                    return {};
                }

                if (questionType === 'get-data' && !this.isSeparateResponseQuestion) {
                    if (Array.isArray(questionResponse)) {
                        return questionResponse.reduce((acc, item) => {
                            if (item.id) {
                                acc[item.id] = item.value;
                            }
                            return acc;
                        }, {});
                    }
                    return {};
                }

                if (questionType === 'check-boxes') {
                    // For checkboxes, we need to handle multiple selected options
                    if (!Array.isArray(questionResponse)) {
                        return {};
                    }

                    // Find the currently selected option we're showing get-data-questions for
                    const selectedOption = this.currentQuestion.options.find(opt =>
                        this.isOptionSelected(opt) && opt.isGetDataEnabled
                    );

                    if (!selectedOption) return {};


                    // Find the matching response for this option
                    const optionResponse = questionResponse?.find(resp =>
                        resp?.value?.id === selectedOption?._id
                    );

                    if (!optionResponse?.getDataResponses) return {};

                    // Convert the getDataResponses to the expected format
                    return optionResponse.getDataResponses.reduce((acc, gdq) => {
                        acc[gdq.id] = gdq.value;
                        return acc;
                    }, {});
                } else {
                    // Original logic for radio buttons
                    if (!questionResponse?.getDataResponses) return {};

                    return questionResponse.getDataResponses.reduce((acc, gdq) => {
                        acc[gdq.id] = gdq.value;
                        return acc;
                    }, {});
                }
            },
            set(newValue) {
                console.log('trigger newValue', newValue);
                const questionType = this.currentQuestion.questionType;

                // For get-data questions with separate farm responses
                if (questionType === 'get-data' && this.isSeparateResponseQuestion && this.currentFarmId) {
                    const farmId = this.currentFarmId;
                    const questionId = this.currentQuestion._id;
                    
                    if (!this.farmResponses[farmId]) {
                        this.$set(this.farmResponses, farmId, {});
                    }
                    
                    // Convert object format back to array format
                    const responseArray = Object.entries(newValue).map(([id, value]) => {
                        const gdq = this.currentQuestion.getDataQuestions.find(q => q._id === id);
                        return {
                            id: id,
                            title: gdq?.title || '',
                            dataType: gdq?.dataType || '',
                            value: value
                        };
                    });
                    
                    this.$set(this.farmResponses[farmId], questionId, responseArray);
                    return;
                }

                if (questionType === 'check-boxes') {
                    // For checkboxes, update the specific option's getDataResponses
                    const selectedOption = this.currentQuestion.options.find(opt =>
                        this.isOptionSelected(opt) && opt.isGetDataEnabled
                    );

                    if (!selectedOption) return;

                    const responseIndex = this.activeQuestionResponse.findIndex(resp =>
                        resp.value.id === selectedOption._id
                    );

                    if (responseIndex === -1) return;

                    const updatedResponses = this.activeQuestionResponse[responseIndex].getDataResponses?.map(gdq => {
                        if (gdq.id in newValue) {
                            return { ...gdq, value: newValue[gdq.id] };
                        }
                        return gdq;
                    });

                    // Update the specific option's getDataResponses
                    this.$set(
                        this.activeQuestionResponse[responseIndex],
                        'getDataResponses',
                        updatedResponses
                    );
                } else {
                    // Original logic for radio buttons
                    if (!this.activeQuestionResponse?.getDataResponses) return;

                    const updatedResponses = this.activeQuestionResponse.getDataResponses?.map(gdq => {
                        if (gdq.id in newValue) {
                            return { ...gdq, value: newValue[gdq.id] };
                        }
                        return gdq;
                    });
                    this.$set(this.activeQuestionResponse, 'getDataResponses', updatedResponses);
                }
            }
        },
        radioModel: {
            get() {
                if (!this.isSeparateResponseQuestion) {
                return this.activeQuestionResponse?.value ?? null;
                }
                const farmResponse = this.currentFarmResponse;
                if (!farmResponse) return null;
                if (farmResponse.value && farmResponse.value.id) {
                    return farmResponse.value.id;
                }
                return farmResponse.value ?? null;
            },
            set(val) {
                if (!this.isSeparateResponseQuestion) {
                if (!this.currentQuestion) return;
                if (!this.activeQuestionResponse) {
                    this.activeQuestionResponse = { value: null, getDataResponses: [], actionPlanResponses: [] };
                }
                this.$set(this.activeQuestionResponse, 'value', val);
                } else {
                    const selectedOption = this.currentQuestion.options.find(opt => opt._id === val);
                    if (!selectedOption) return;
                    const farmId = this.currentFarmId;
                    const questionId = this.currentQuestion._id;
                    if (!this.farmResponses[farmId]) {
                        this.$set(this.farmResponses, farmId, {});
                    }
                    this.$set(this.farmResponses[farmId], questionId, {
                        value: {
                            id: val,
                            label: selectedOption.label,
                            isActionPlanEnabled: selectedOption.isActionPlanEnabled,
                            isGetDataEnabled: selectedOption.isGetDataEnabled,
                            isDimitraModulesEnabled: selectedOption.isDimitraModulesEnabled
                        },
                        actionPlanResponses: [],
                        getDataResponses: [],
                        farmIds: [farmId]
                    });
                }
            }
        },
        isSeparateResponseQuestion() {
            return this.currentQuestion?.respondentType === 'Farmer (Separate response for each farm)';
        },
        isSingleResponseQuestion() {
            return [
                'Farmer (Single response for all farms)',
                'Connected Farmer',
                'Connected Cacao',
            ].includes(this.currentQuestion?.respondentType);
        },
        availableFarms() {
            if (!this.isSeparateResponseQuestion || !this.currentQuestion?.farms) {
                return [];
            }
            return this.currentQuestion.farms || [];
        },
        currentFarmIndex() {
            if (!this.currentFarmId || !this.availableFarms.length) return 0;
            return this.availableFarms.findIndex(farm => farm._id === this.currentFarmId);
        },
        currentFarmResponse: {
            get() {
                if (!this.isSeparateResponseQuestion) {
                    return this.activeQuestionResponse;
                }
                const questionId = this.currentQuestion?._id;
                const farmId = this.currentFarmId;
                if (!questionId || !farmId) return null;
                
                if (this.farmResponses[farmId] && this.farmResponses[farmId][questionId] !== undefined) {
                    return this.farmResponses[farmId][questionId];
                }
                if (this.currentQuestion?.response && Array.isArray(this.currentQuestion.response)) {
                    const farmResponses = this.currentQuestion.response.filter(resp => 
                        resp.farmIds && resp.farmIds.includes(farmId)
                    );
                    
                    if (this.currentQuestion.questionType === 'check-boxes') {
                        return farmResponses.length > 0 ? farmResponses : [];
                    } else if (this.currentQuestion.questionType === 'radio-button') {
                        return farmResponses.length > 0 ? farmResponses[0] : null;
                    } else {
                        return farmResponses.length > 0 ? farmResponses[0] : null;
                    }
                }
                if (this.currentQuestion.questionType === 'check-boxes') {
                    return [];
                }
                return null;
            },
            set(value) {
                if (!this.isSeparateResponseQuestion) {
                    this.activeQuestionResponse = value;
                    return;
                }
                
                const questionId = this.currentQuestion?._id;
                const farmId = this.currentFarmId;
                
                if (!questionId || !farmId) return;
                
                if (!this.farmResponses[farmId]) {
                    this.$set(this.farmResponses, farmId, {});
                }
                
                this.$set(this.farmResponses[farmId], questionId, value);
            }
        },
    },
    watch: {
        currentQuestionIndex(newIndex, oldIndex) {
            window.scrollTo({
                top: this.$refs.surveyContent?.getBoundingClientRect().top + window.scrollY - 60,
                behavior: 'smooth'
            });
            const q = this.questions[newIndex];
            if (q && q.uploadedFiles === undefined) {
                this.$set(q, 'uploadedFiles', []);
                this.replaceRawQuestion(q._id, { uploadedFiles: [] });
            }
            if (this.isSeparateResponseQuestion && this.availableFarms.length > 0) {
                if (!this.currentFarmId || newIndex !== oldIndex) {
                    this.currentFarmId = this.availableFarms[0]._id;
                    this.loadFarmResponse();
                }
            } else {
                this.currentFarmId = null;
            }
            this.syncRepeatState();
        },
    
        currentFarmId(newFarmId, oldFarmId) {
            if (newFarmId && newFarmId !== oldFarmId && this.isSeparateResponseQuestion) {
                this.loadFarmResponse();
            }
        },
        selectedDateIndex(newIndex, oldIndex) {
            if (newIndex === oldIndex) return;
            if (this.hasRepeatSubmissions) {
                this.applyRepeatResponseForSelectedDate();
            }
        }
    },

    mounted() {
        this.loadQuestions()
            .then(() => (this.isLoading = false))
            .catch(() => (this.isLoading = false));
    },
    methods: {
        cloneRepeatValue(value) {
            if (value === null || value === undefined) return value;
            return JSON.parse(JSON.stringify(value));
        },
        getEmptyQuestionResponse(question = this.currentQuestion) {
            if (!question) return null;
            if (question.questionType === 'check-boxes' || question.questionType === 'get-data') {
                return [];
            }
            if (question.questionType === 'radio-button') {
                return { value: null, getDataResponses: [], actionPlanResponses: [] };
            }
            return null;
        },
        normalizeResponseForQuestion(question, responsePayload) {
            if (!question) return null;
            const normalizedPayload = this.cloneRepeatValue(responsePayload);
            switch (question.questionType) {
                case 'text-field':
                case 'text-area':
                    if (typeof normalizedPayload === 'object' && normalizedPayload !== null && 'value' in normalizedPayload) {
                        return normalizedPayload.value ?? '';
                    }
                    return normalizedPayload ?? '';
                case 'numeric':
                    if (typeof normalizedPayload === 'object' && normalizedPayload !== null && 'value' in normalizedPayload) {
                        return normalizedPayload.value !== null && normalizedPayload.value !== undefined
                            ? Number(normalizedPayload.value)
                            : null;
                    }
                    return normalizedPayload !== null && normalizedPayload !== undefined
                        ? Number(normalizedPayload)
                        : null;
                case 'date':
                    if (typeof normalizedPayload === 'object' && normalizedPayload !== null && 'value' in normalizedPayload) {
                        return normalizedPayload.value ?? null;
                    }
                    return normalizedPayload ?? null;
                case 'radio-button': {
                    const valuePayload = normalizedPayload?.value ?? normalizedPayload;
                    return {
                        value: valuePayload?.id || valuePayload || null,
                        getDataResponses: normalizedPayload?.getDataResponses?.map(gdq => ({
                            ...gdq,
                            response: gdq.value
                        })) || [],
                        actionPlanResponses: normalizedPayload?.actionPlanResponses?.map(ap => ({
                            ...ap,
                            response: ap.value
                        })) || []
                    };
                }
                case 'check-boxes':
                    return Array.isArray(normalizedPayload) ? normalizedPayload : [];
                case 'attach-files':
                case 'digital-signature':
                    return normalizedPayload || null;
                default:
                    return normalizedPayload || (question.questionType === 'get-data' ? [] : null);
            }
        },
        extractUploadedFilesFromResponse(question, responsePayload) {
            if (!question || question.questionType !== 'attach-files') return [];
            const fileValue = responsePayload?.value;
            if (!Array.isArray(fileValue)) return [];
            return fileValue
                .filter(attachment => attachment?.key)
                .map(attachment => ({
                    name: attachment.fileName || attachment.key?.split('/').pop(),
                    s3Key: attachment.key,
                    attachmentLink: attachment.fileUrl,
                    comment: attachment.comment || ''
                }));
        },
        extractSignatureFromResponse(question, responsePayload) {
            if (!question || question.questionType !== 'digital-signature') return null;
            const sigValue = responsePayload?.value;
            if (!sigValue?.attachmentLink) return null;
            return {
                attachmentLink: sigValue.attachmentLink,
                s3key: sigValue.key
            };
        },
        syncQuestionViewStateFromResponse(question, responsePayload) {
            if (!question || !Array.isArray(question.options)) return;
            const normalizedResponse = this.normalizeResponseForQuestion(question, responsePayload);
            question.options = question.options.map(option => {
                const nextOption = {
                    ...option,
                    selected: false
                };
                if (nextOption.actionPlans) {
                    nextOption.actionPlans = nextOption.actionPlans.map(plan => ({
                        ...plan,
                        uploadedFiles: [],
                        files: []
                    }));
                }
                return nextOption;
            });

            if (question.questionType === 'radio-button') {
                const selectedValue = normalizedResponse?.value ?? null;
                if (!selectedValue) return;
                question.options = question.options.map(option => {
                    if (option._id !== selectedValue || !option.actionPlans) return option;
                    const actionPlanResponses = normalizedResponse?.actionPlanResponses || [];
                    return {
                        ...option,
                        actionPlans: option.actionPlans.map(plan => {
                            const actionPlanResponse = actionPlanResponses.find(ap => ap.id === plan._id);
                            if (actionPlanResponse && actionPlanResponse.files) {
                                return {
                                    ...plan,
                                    uploadedFiles: actionPlanResponse.files.map(file => ({
                                        name: file.name,
                                        s3Key: file.key,
                                        fileUrl: file.fileUrl,
                                        comment: file.comment || ''
                                    })),
                                    files: actionPlanResponse.files
                                };
                            }
                            return { ...plan, uploadedFiles: [], files: [] };
                        })
                    };
                });
                return;
            }

            if (question.questionType === 'check-boxes') {
                const checkboxResponses = Array.isArray(normalizedResponse) ? normalizedResponse : [];
                question.options = question.options.map(option => {
                    const optionResponse = checkboxResponses.find(resp => resp?.value?.id === option._id);
                    const nextOption = {
                        ...option,
                        selected: !!optionResponse
                    };
                    if (nextOption.actionPlans) {
                        nextOption.actionPlans = nextOption.actionPlans.map(plan => {
                            const actionPlanResponse = optionResponse?.actionPlanResponses?.find(ap => ap.id === plan._id);
                            if (actionPlanResponse && actionPlanResponse.files) {
                                return {
                                    ...plan,
                                    uploadedFiles: actionPlanResponse.files.map(file => ({
                                        name: file.name,
                                        s3Key: file.key,
                                        fileUrl: file.fileUrl,
                                        comment: file.comment || ''
                                    })),
                                    files: actionPlanResponse.files
                                };
                            }
                            return { ...plan, uploadedFiles: [], files: [] };
                        });
                    }
                    return nextOption;
                });
            }
        },
        getRepeatDraftKey(question, index = this.selectedDateIndex) {
            if (!question || index === null || index === undefined || index < 0) return '';
            return `${question._id}::${index}`;
        },
        buildRepeatDraftState(question, index = this.selectedDateIndex) {
            if (!question) return null;
            const responses = Array.isArray(question._repeatResponses) ? question._repeatResponses : [];
            const responseEntry = responses[index] || null;
            const normalizedResponse = this.normalizeResponseForQuestion(
                question,
                this.extractAssessmentResponse(responseEntry)
            );
            return {
                response: normalizedResponse,
                uploadedFiles: this.extractUploadedFilesFromResponse(question, normalizedResponse),
                signature: this.extractSignatureFromResponse(question, normalizedResponse)
            };
        },
        ensureRepeatDraft(question = this.currentQuestion, index = this.selectedDateIndex) {
            if (!question || !Array.isArray(question.repetitionDates) || !question.repetitionDates.length) return null;
            const draftKey = this.getRepeatDraftKey(question, index);
            if (!draftKey) return null;
            if (!Object.prototype.hasOwnProperty.call(this.repeatDraftResponses, draftKey)) {
                this.$set(this.repeatDraftResponses, draftKey, this.buildRepeatDraftState(question, index));
            }
            return this.repeatDraftResponses[draftKey];
        },
        clearRepeatDraft(question = this.currentQuestion, index = this.selectedDateIndex) {
            const draftKey = this.getRepeatDraftKey(question, index);
            if (!draftKey || !Object.prototype.hasOwnProperty.call(this.repeatDraftResponses, draftKey)) return;
            this.$delete(this.repeatDraftResponses, draftKey);
        },
        goToPreviousFarm() {
            if (this.currentFarmIndex > 0) {
                this.currentFarmId = this.availableFarms[this.currentFarmIndex - 1]._id;
                this.loadFarmResponse();
            }
        },
        goToNextFarm() {
            if (this.currentFarmIndex < this.availableFarms.length - 1) {
                this.currentFarmId = this.availableFarms[this.currentFarmIndex + 1]._id;
                this.loadFarmResponse();
            }
        },
        loadFarmResponse() {
            if (!this.isSeparateResponseQuestion || !this.currentFarmId || !this.currentQuestion) {
                return;
            }
            const farmId = this.currentFarmId;
            if (this.currentQuestion.response && Array.isArray(this.currentQuestion.response)) {
                const farmResponses = this.currentQuestion.response.filter(resp => 
                    Array.isArray(resp.farmIds) && resp.farmIds.includes(farmId)
                );
                // Handle different question types
                if (this.currentQuestion.questionType === 'check-boxes') {
                    // Update checkbox options based on farm response
                    if (this.currentQuestion.options) {
                        this.currentQuestion.options.forEach(option => {
                            const hasResponse = farmResponses.some(resp => 
                                resp.value && resp.value.id === option._id
                            );
                            this.$set(option, 'selected', hasResponse);
                        });
                    }
                } else if (this.currentQuestion.questionType === 'radio-button') {
                    // Radio button responses are handled by currentFarmResponse getter/setter
                } else if (this.currentQuestion.questionType === 'text-field' || 
                        this.currentQuestion.questionType === 'text-area' ||
                        this.currentQuestion.questionType === 'numeric' ||
                        this.currentQuestion.questionType === 'date') {
                    // Text-based responses are handled by currentFarmResponse getter/setter
                }
            }
            this.$forceUpdate();
        },
        syncRepeatState() {
            if (!this.hasRepeatSubmissions) return;
            const nextIndex = this.getDefaultRepeatIndex();
            if (this.selectedDateIndex !== nextIndex) {
                this.selectedDateIndex = nextIndex;
                return;
            }
            this.applyRepeatResponseForSelectedDate();
        },
        selectRepeatDate(index) {
            if (this.isRepeatUpcoming(index)) return;
            this.selectedDateIndex = index;
        },
        getDefaultRepeatIndex() {
            const activeIndex = this.repetitionDates.findIndex((_, index) =>
                this.isRepeatWindowOpen(index)
            );
            if (activeIndex >= 0) return activeIndex;
            return 0;
        },
        applyRepeatResponseForSelectedDate() {
            const question = this.currentQuestion;
            if (!question || !this.hasRepeatSubmissions) return;
            this.resetQuestionForRepeatView(question);
            const responseEntry = this.activeRepeatResponse;
            question.rejectionReason = responseEntry?.rejectionReason || null;
            question.submittedOn = this.getRepeatResponseSubmittedOn(responseEntry);
            const draft = this.ensureRepeatDraft(question, this.selectedDateIndex);
            if (!draft) return;
            this.hydrateQuestionFromResponse(question, draft.response);
        },
        resetQuestionForRepeatView(question) {
            if (!question) return;
            if (Array.isArray(question.options)) {
                question.options = question.options.map(option => {
                    this.$set(option, 'selected', false);
                    if (option.actionPlans) {
                        option.actionPlans = option.actionPlans.map(plan => ({
                            ...plan,
                            uploadedFiles: [],
                            files: []
                        }));
                    }
                    return option;
                });
            }
        },
        hydrateQuestionFromResponse(question, responsePayload) {
            if (!question) return;
            this.syncQuestionViewStateFromResponse(question, responsePayload);
        },
        extractAssessmentResponse(repeatResponse) {
            if (!repeatResponse) return null;
            if (repeatResponse.assessmentResponse) return repeatResponse.assessmentResponse;
            if (repeatResponse.response) return repeatResponse.response;
            if (Object.prototype.hasOwnProperty.call(repeatResponse, 'value') ||
                repeatResponse.getDataResponses ||
                repeatResponse.actionPlanResponses) {
                return repeatResponse;
            }
            return null;
        },
        getRepeatResponseByDate(date) {
            if (!date || !this.currentQuestion) return null;
            const responses = this.currentQuestion._repeatResponses;
            if (!Array.isArray(responses)) return null;
            const targetKey = this.repeatDateKey(date);
            if (!targetKey) return null;
            let matched = null;
            responses.forEach(resp => {
                const respDate = resp?.repetitionDate || resp?.repetitiondate || resp?.repeatDate;
                const key = this.repeatDateKey(respDate);
                if (key && key === targetKey) {
                    matched = resp;
                }
            });
            return matched;
        },
        getRepeatResponseSubmittedOn(repeatResponse) {
            if (!repeatResponse) return null;
            return repeatResponse.submittedOn ||
                repeatResponse.submittedAt ||
                repeatResponse.createdAt ||
                repeatResponse.updatedAt ||
                repeatResponse.assessmentResponse?.submittedOn ||
                repeatResponse.response?.submittedOn ||
                null;
        },
        normalizeRepeatDate(date) {
            if (!date) return null;
            const normalized = moment(date).startOf('day');
            return normalized.isValid() ? normalized : null;
        },
        repeatDateKey(date) {
            if (!date) return '';
            const normalized = this.normalizeRepeatDate(date);
            if (normalized) return normalized.format('YYYY-MM-DD');
            return String(date);
        },
        mapRepeatResponsesToDates(responses, repetitionDates) {
            if (!Array.isArray(responses)) return [];
            const ordered = Array.isArray(repetitionDates)
                ? repetitionDates.map(() => null)
                : [];
            const responseMap = new Map();
            const undated = [];

            responses.forEach(resp => {
                const respDate = resp?.repetitionDate || resp?.repetitiondate || resp?.repeatDate;
                const key = this.repeatDateKey(respDate);
                if (!key) {
                    undated.push(this.cloneRepeatValue(resp));
                    return;
                }
                responseMap.set(key, this.cloneRepeatValue(resp));
            });

            (repetitionDates || []).forEach((date, index) => {
                const key = this.repeatDateKey(date);
                if (!key) return;
                if (responseMap.has(key)) {
                    ordered[index] = responseMap.get(key);
                    responseMap.delete(key);
                }
            });

            responseMap.forEach(resp => ordered.push(resp));
            undated.forEach(resp => ordered.push(resp));
            return ordered;
        },
        getRepeatResponseDisplayValue(responsePayload) {
            if (!responsePayload || typeof responsePayload !== 'object') return '';
            const value = responsePayload.value;
            if (value === null || value === undefined) return '';
            if (Array.isArray(value)) {
                return value
                    .map(item => (item === null || item === undefined ? '' : String(item)))
                    .filter(Boolean)
                    .join(', ');
            }
            if (typeof value === 'object') {
                if (typeof value.label === 'string') return value.label;
                if (typeof value.name === 'string') return value.name;
                if (typeof value.id === 'string' || typeof value.id === 'number') return String(value.id);
                return '';
            }
            return String(value);
        },
        isSameRepeatDate(dateA, dateB) {
            if (!dateA || !dateB) return false;
            const a = this.normalizeRepeatDate(dateA);
            const b = this.normalizeRepeatDate(dateB);
            if (a && b) {
                return a.isSame(b, 'day');
            }
            return dateA === dateB;
        },
        isRepeatWindowOpen(index) {
            if (!this.repetitionDates.length) return false;
            const current = moment().startOf('day');
            const startDate = this.normalizeRepeatDate(this.repetitionDates[index]);
            if (!startDate) return false;
            const nextDateValue = this.repetitionDates[index + 1];
            if (nextDateValue) {
                const nextDate = this.normalizeRepeatDate(nextDateValue);
                if (!nextDate) return current.isSameOrAfter(startDate);
                return current.isSameOrAfter(startDate) && current.isBefore(nextDate);
            }
            return current.isSameOrAfter(startDate);
        },
        isRepeatUpcoming(index) {
            if (!this.repetitionDates.length) return false;
            const current = moment().startOf('day');
            const date = this.normalizeRepeatDate(this.repetitionDates[index]);
            if (!date) return false;
            return current.isBefore(date);
        },
        async loadQuestions() {
            try {
                this.startLoading();
                const goal = await EsgService.getEsgGoal(this.goalId);
                this.goalTitle = goal?.data?.title || '';
                this.currentAssessmentId = goal.data.esgAssessment;
                this.currentGoalId = goal.data._id;
                this.esgProtocolId = goal.data.esgProtocol;
                this.esgIssueId = goal.data.esgIssue;
                this.esgSubOrganization = this.$route.query?.subOrganizationId || goal.data.organization;

                const response = await EsgService.getSurveyResponsesByQuestionIdAndUserId(
                    this.surveyId,
                    this.currentGoalId,
                    {
                        questionId: this.$route.query?.questionId,
                    }
                );
                const rawData = response?.data || [];
                rawData.forEach(heading => {
                    if (heading.assessmentQuestions) {
                        heading.assessmentQuestions.forEach(question => {
                            if (!question.deletedAt) {
                                this.initializeQuestion(question);
                            if (question.respondentType === 'Farmer (Separate response for each farm)' && question.farms) {
                                this.initializeFarmResponses(question);
                            }
                            }
                        });
                    }
                });
                this.rawQuestions = response?.data || [];
                this.loadFromLocalStorage();

                // Set initial question index
                if (this.questions.length > 0) {
                    this.currentQuestionIndex = 0;
                if (this.isSeparateResponseQuestion && this.availableFarms.length > 0) {
                    this.currentFarmId = this.availableFarms[0]._id;
                }
                    this.syncRepeatState();
                }

                console.log('Loaded questions with responses:', this.questions);
            } catch (error) {
                console.error('Error loading questions:', error);
            } finally {
                this.stopLoading();
            }
        },
        initializeFarmResponses(question) {
            if (!question.farms || !question.response) return;
            
            if (Array.isArray(question.response)) {
                question.response.forEach(resp => {
                    if (resp.farmIds && Array.isArray(resp.farmIds)) {
                        resp.farmIds.forEach(farmId => {
                            if (!this.farmResponses[farmId]) {
                                this.$set(this.farmResponses, farmId, {});
                            }
                            this.$set(this.farmResponses[farmId], question._id, resp);
                        });
                    }
                });
            }
        },

        initializeQuestion(q) {
            if (!q) return;
            const hasRepeat = Array.isArray(q.repetitionDates) && q.repetitionDates.length > 0;
            if (hasRepeat) {
                q._repeatResponses = this.mapRepeatResponsesToDates(
                    Array.isArray(q.response) ? q.response : [],
                    q.repetitionDates
                );
                q.response = null;
                q.rejectionReason = null;
                q.submittedOn = null;
                if (!q.uploadedFiles) {
                    q.uploadedFiles = [];
                }
                q.signature = null;
                return;
            }

            if (q.response !== null && q.response !== undefined && typeof q._originalResponse === 'undefined') {
                q._originalResponse = q.response;
            }

            // Initialize base response structure
            if (!q.response) {
                q.response = q.questionType === 'check-boxes' ? [] : null;
                q.uploadedFiles = [];
                q.signature = null;
            }

            if (q.respondentType === 'Farmer (Separate response for each farm)') {
                if (!q.uploadedFiles) {
                    q.uploadedFiles = [];
                }
                return; 
            }

            const isFarmerResponse = Array.isArray(q.response);
            let normalizedResponse = q.response;
            
            if (isFarmerResponse && q.questionType !== 'check-boxes') {
                if (q.response.length > 0) {
                    normalizedResponse = q.response[0];
                } else {
                    normalizedResponse = null;
                }
            }

            // Handle pre-filling responses based on question type
            switch (q.questionType) {
                case 'text-field':
                case 'text-area':
                case 'numeric':
                case 'date':
                    if (isFarmerResponse) {
                        if (normalizedResponse && normalizedResponse.value !== undefined) {
                            q._originalResponse = q.response;
                            q.response = normalizedResponse.value;
                        }
                    } else if (q.response && typeof q.response === 'object' && 'value' in q.response) {
                        const value = q.response.value;
                        q._originalResponse = q.response;
                        q.response = value;
                    }
                    break;

                case 'radio-button':
                    if (isFarmerResponse) {
                        // Farmer format: response is array
                        if (normalizedResponse && normalizedResponse.value) {
                            const response = {
                                value: normalizedResponse.value?.id || normalizedResponse.value,
                                getDataResponses: normalizedResponse.getDataResponses?.map(gdq => ({
                                    ...gdq,
                                    response: gdq.value
                                })),
                                actionPlanResponses: normalizedResponse.actionPlanResponses?.map(ap => ({
                                    ...ap,
                                    response: ap.value
                                }))
                            };
                            q._originalResponse = q.response;
                            q.response = response;

                            // Initialize action plans' uploadedFiles
                            if (q.options && q.response.actionPlanResponses) {
                                q.options = q.options.map(option => {
                                    if (option.actionPlans) {
                                        option.actionPlans = option.actionPlans.map(plan => {
                                            const actionPlanResponse = q.response.actionPlanResponses.find(
                                                ap => ap.id === plan._id
                                            );
                                            if (actionPlanResponse && actionPlanResponse.files) {
                                                return {
                                                    ...plan,
                                                    uploadedFiles: actionPlanResponse.files.map(file => ({
                                                        name: file.name,
                                                        s3Key: file.key,
                                                        fileUrl: file.fileUrl,
                                                        comment: file.comment || ''
                                                    })),
                                                    files: actionPlanResponse.files
                                                };
                                            }
                                            return { ...plan, uploadedFiles: [], files: [] };
                                        });
                                    }
                                    return option;
                                });
                            }
                        }
                    } else if (
                        q.response &&
                        typeof q.response.value !== 'undefined' &&
                        q.response.value !== null
                    ) {
                        const response = {
                            value: q.response?.value?.id || q.response?.value,
                            getDataResponses: q.response?.getDataResponses?.map(gdq => ({
                                ...gdq,
                                response: gdq.value
                            })),
                            actionPlanResponses: q.response?.actionPlanResponses?.map(ap => ({
                                ...ap,
                                response: ap.value
                            }))
                        };
                        q.response = response;

                        // Initialize action plans' uploadedFiles
                        if (q.options && q.response.actionPlanResponses) {
                            q.options = q.options.map(option => {
                                if (option.actionPlans) {
                                    option.actionPlans = option.actionPlans.map(plan => {
                                        const actionPlanResponse = q.response.actionPlanResponses.find(
                                            ap => ap.id === plan._id
                                        );
                                        if (actionPlanResponse && actionPlanResponse.files) {
                                            return {
                                                ...plan,
                                                uploadedFiles: actionPlanResponse.files.map(file => ({
                                                    name: file.name,
                                                    s3Key: file.key,
                                                    fileUrl: file.fileUrl,
                                                    comment: file.comment || ''
                                                })),
                                                files: actionPlanResponse.files
                                            };
                                        }
                                        return { ...plan, uploadedFiles: [], files: [] };
                                    });
                                }
                                return option;
                            });
                        }
                    }
                    break;

                case 'check-boxes':
                    if (Array.isArray(q.response)) {
                        q._originalResponse = q.response;
                        q.response = q.response?.map(item => item);
                    }

                    if (Array.isArray(q.options)) {
                        q.options = q.options?.map(option => {
                            option.selected = q.response?.some(item => item?.value?.id === option._id);

                            // Initialize action plans' uploadedFiles for this option
                            if (option.actionPlans) {
                                const optionResponse = q.response.find(
                                    resp => resp?.value?.id === option._id
                                );
                                option.actionPlans = option.actionPlans.map(plan => {
                                    if (!plan._id) {
                                        console.warn('Action plan missing _id:', plan);
                                    }
                                    if (optionResponse && optionResponse.actionPlanResponses) {
                                        const actionPlanResponse = optionResponse.actionPlanResponses.find(
                                            ap => ap.id === plan._id
                                        );
                                        if (actionPlanResponse && actionPlanResponse.files) {
                                            return {
                                                ...plan,
                                                uploadedFiles: actionPlanResponse.files.map(file => ({
                                                    name: file.name,
                                                    s3Key: file.key,
                                                    fileUrl: file.fileUrl,
                                                    comment: file.comment || ''
                                                })),
                                                files: actionPlanResponse.files
                                            };
                                        }
                                    }
                                    return { ...plan, uploadedFiles: [], files: [] };
                                });
                            }

                            // Initialize getData questions if enabled
                            if (option.isGetDataEnabled && option.getDataQuestions) {
                                option.getDataQuestions = option.getDataQuestions.map(q => ({
                                    ...q,
                                    response: null,
                                    files: [],
                                    uploadedFiles: []
                                }));
                            }

                            return option;
                        });
                    }
                    break;

                case 'attach-files':
                if (q.respondentType === 'Farmer (Separate response for each farm)') {
                        q.uploadedFiles = this.normalizeAttachFiles(q.uploadedFiles || []);
                    break;
                }
                {
                    const fileValue = isFarmerResponse ? normalizedResponse?.value : q.response?.value;
                    if (fileValue && Array.isArray(fileValue)) {
                        q.uploadedFiles = this.normalizeAttachFiles(fileValue);
                    } else {
                        q.uploadedFiles = [];
                    }
                    }
                    break;

                case 'digital-signature': {
                    const sigValue = isFarmerResponse ? normalizedResponse?.value : q.response?.value;
                    if (sigValue?.attachmentLink) {
                        q.signature = {
                            attachmentLink: sigValue.attachmentLink,
                            s3key: sigValue.key
                        };
                    }
                    }
                    break;
            }
        },

        // --- Navigation Methods ---
        goToSurveyOverview() {
            if (window.history.length > 1) {
                this.$router.go(-1);
            } else {
            this.$router.push({ name: 'ProtocolDetailsOfSubOrganization', params: { id: this.esgProtocolId, subOrganizationId: this.esgSubOrganization } });
            }
        },

        loadFromLocalStorage() {
            const key = `survey_responses_${this.surveyId}_${this.goalId}`;
            const savedResponses = JSON.parse(localStorage.getItem(key)) || {};
            this.rawQuestions.forEach(heading => {
                if (heading.assessmentQuestions) {
                    heading.assessmentQuestions.forEach(question => {
                        const hasRepeat = Array.isArray(question.repetitionDates) && question.repetitionDates.length > 0;
                        if (hasRepeat) return;
                        if (savedResponses[question._id] !== undefined) {
                            question.response = savedResponses[question._id];
                            if (question.questionType === 'attach-files') {
                                question.uploadedFiles = this.getAttachFilesFromResponse(question.response);
                            }
                        }
                    });
                }
            });
        },

        saveToLocalStorage(question) {
            if (!question) return;
            if (Array.isArray(question.repetitionDates) && question.repetitionDates.length > 0) return;
            const key = `survey_responses_${this.surveyId}_${this.goalId}`;
            let responses = JSON.parse(localStorage.getItem(key)) || {};
            
            // For attach-files questions, convert uploadedFiles to response.value format
            if (question.questionType === 'attach-files' && this.getAttachFilesForQuestion(question).length > 0) {
                responses[question._id] = {
                    value: this.mapAttachFilesToResponseValue(this.getAttachFilesForQuestion(question))
                };
            } else {
                responses[question._id] = question.response;
            }
            
            localStorage.setItem(key, JSON.stringify(responses));
        },

        // --- File Handling Methods ---
        onQuestionFileChange(event, question) {
            const input = event.target;
            const files = Array.from(input?.files || []);
            this.handleQuestionFileChange(files, question);
            if (input) {
                input.value = null;
            }
        },
        handleQuestionDrop(event, question) {
            event.preventDefault();
            this.dragging = false;
            const files = Array.from(event.dataTransfer.files);
            this.handleQuestionFileChange(files, question);
        },
        async handleQuestionFileChange(files, question) {
            await this.handleFileUpload(files, question, 'uploadedFiles');
        },
        getFileAcceptTypes(allowedTypes) {
            if (!allowedTypes) return undefined;
            const typeMap = { image: 'image/*', video: 'video/*', docx: '.doc,.docx', pdf: '.pdf' };
            return Object.entries(allowedTypes)
                .filter(([allowed]) => allowed)
                .map(([type]) => typeMap[type])
                .join(',');
        },
        getAttachFileSettings(question = this.currentQuestion) {
            return question?.additionalSetting ||
                question?.fileTypeAdditionalSettings ||
                question?.additionalSettings ||
                {};
        },
        canShowAttachFileComments(question = this.currentQuestion) {
            const settings = this.getAttachFileSettings(question);
            if (typeof settings.allowComments !== 'undefined') {
                return Boolean(settings.allowComments);
            }
            return Boolean(question?.allowComments);
        },
        normalizeAttachFile(file = {}) {
            if (!file || typeof file !== 'object') return null;
            const name = file.name || file.fileName || file.originalname || file.key?.split('/').pop() || '';
            const s3Key = file.s3Key || file.key || '';
            const attachmentLink = file.attachmentLink || file.fileUrl || file.s3Location || '';

            if (!name && !s3Key && !attachmentLink && !file.isUploading) {
                return null;
            }

            return {
                ...file,
                name,
                fileName: file.fileName || name,
                s3Key,
                key: file.key || s3Key,
                attachmentLink,
                fileUrl: file.fileUrl || attachmentLink,
                s3Location: file.s3Location || attachmentLink,
                comment: file.comment || ''
            };
        },
        normalizeAttachFiles(files = []) {
            if (!Array.isArray(files)) return [];
            return files.map(file => this.normalizeAttachFile(file)).filter(Boolean);
        },
        mapAttachFilesToResponseValue(files = []) {
            return this.normalizeAttachFiles(files)
                .filter(file => file.isUploading !== true)
                .map(file => ({
                    key: file.s3Key || file.key,
                    fileUrl: file.attachmentLink || file.fileUrl || file.s3Location || '',
                    fileName: file.name || file.fileName || '',
                    comment: file.comment || ''
                }));
        },
        getAttachFilesFromResponse(responsePayload) {
            return this.normalizeAttachFiles(
                Array.isArray(responsePayload?.value) ? responsePayload.value : []
            );
        },
        getRawQuestionRef(questionId = this.currentQuestion?._id) {
            if (!questionId) return null;
            for (let headingIndex = 0; headingIndex < this.rawQuestions.length; headingIndex += 1) {
                const heading = this.rawQuestions[headingIndex];
                const questionIndex = (heading.assessmentQuestions || []).findIndex(question => question._id === questionId);
                if (questionIndex !== -1) {
                    return {
                        heading,
                        headingIndex,
                        questionIndex,
                        question: heading.assessmentQuestions[questionIndex]
                    };
                }
            }
            return null;
        },
        syncQuestionSnapshot(question, payload = {}) {
            if (!question) return;
            Object.entries(payload).forEach(([key, value]) => {
                this.$set(question, key, value);
            });
        },
        replaceRawQuestion(questionId, payload = {}) {
            const ref = this.getRawQuestionRef(questionId);
            if (!ref) return null;
            const nextQuestion = {
                ...ref.question,
                ...payload
            };
            this.$set(ref.heading.assessmentQuestions, ref.questionIndex, nextQuestion);
            return nextQuestion;
        },
        bumpAttachFilesUI() {
            this.uploadKey += 1;
        },
        getAttachFilesForQuestion(question = this.currentQuestion) {
            if (!question || question.questionType !== 'attach-files') return [];

            if (this.isSeparateResponseQuestion && question === this.currentQuestion && this.currentFarmId) {
                const farmId = this.currentFarmId;
                const questionId = question._id;
                const storedResponse = this.farmResponses[farmId]?.[questionId];
                if (storedResponse?.uploadedFiles?.length) {
                    return storedResponse.uploadedFiles;
                }
                const files = this.getAttachFilesFromResponse(storedResponse || this.currentFarmResponse);
                if (storedResponse && !storedResponse.uploadedFiles?.length) {
                    this.$set(storedResponse, 'uploadedFiles', files);
                }
                return files;
            }

            if (this.hasRepeatSubmissions && question === this.currentQuestion) {
                const draft = this.ensureRepeatDraft(question, this.selectedDateIndex);
                if (draft?.uploadedFiles?.length) {
                    return draft.uploadedFiles;
                }
                const files = this.getAttachFilesFromResponse(draft?.response);
                if (draft && !draft.uploadedFiles?.length) {
                    this.$set(draft, 'uploadedFiles', files);
                }
                return files;
            }

            if (question.uploadedFiles?.length) {
                return question.uploadedFiles;
            }

            const files = this.getAttachFilesFromResponse(question.response);
            if (files.length) {
                this.syncQuestionSnapshot(question, { uploadedFiles: files });
                this.replaceRawQuestion(question._id, { uploadedFiles: files });
            }
            return files;
        },
        syncAttachFilesForQuestion(question, files = [], options = {}) {
            if (!question || question.questionType !== 'attach-files') return;
            const { bumpKey = true } = options;
            const normalizedFiles = this.normalizeAttachFiles(files);
            const responsePayload = {
                id: question._id,
                value: this.mapAttachFilesToResponseValue(normalizedFiles)
            };

            if (this.isSeparateResponseQuestion && question === this.currentQuestion && this.currentFarmId) {
                this.attachFilesModel = normalizedFiles;
                if (bumpKey) this.bumpAttachFilesUI();
                return;
            }

            if (this.hasRepeatSubmissions && question === this.currentQuestion) {
                this.activeUploadedFiles = normalizedFiles;
                if (bumpKey) this.bumpAttachFilesUI();
                return;
            }

            const payload = {
                uploadedFiles: normalizedFiles,
                response: responsePayload
            };

            this.syncQuestionSnapshot(question, payload);
            this.replaceRawQuestion(question._id, payload);

            if (bumpKey) {
                this.bumpAttachFilesUI();
            }
        },
        updateAttachFileComment(index, comment, question = this.currentQuestion) {
            const files = [...this.getAttachFilesForQuestion(question)];
            if (index < 0 || index >= files.length) return;
            files.splice(index, 1, {
                ...files[index],
                comment: comment || ''
            });
            this.syncAttachFilesForQuestion(question, files, { bumpKey: false });
            this.saveToLocalStorage(question);
        },

        async handleFileUpload(files, target, keyProperty) {
            if (!files || files.length === 0) return;

            this.startLoading();
            try {
                target.isUploading = true;
                target.uploadProgress = 0;

                const attachFileSettings = this.getAttachFileSettings(target);
                let currentFiles = this.normalizeAttachFiles(this.getAttachFilesForQuestion(target));
                let successfulUploads = 0;

                for (const [index, file] of files.entries()) {
                    if (!file) continue;
                    try {
                        const result = await S3UploadService.singleUpload(file, 'survey-uploads');
                        const uploadedFile = this.normalizeAttachFile({
                            name: file.name,
                            fileName: file.name,
                            s3Key: result.key,
                            fileUrl: result.fullPath,
                            attachmentLink: result.fullPath,
                            s3Location: result.fullPath,
                            comment: '',
                            isUploading: false
                        });

                        if (attachFileSettings?.allowMultipleAttachments) {
                            currentFiles = [...currentFiles, uploadedFile];
                        } else {
                            currentFiles = [uploadedFile];
                        }

                        this.syncAttachFilesForQuestion(target, currentFiles);
                        target.uploadProgress = Math.min(100, Math.round(((index + 1) / files.length) * 100));
                        successfulUploads += 1;
                    } catch (error) {
                        console.error('Error uploading file:', file.name, error);
                        this.$notify({
                            type: 'error',
                            text: `Failed to upload ${file.name}`
                        });
                    }
                }

                this.$set(target, keyProperty, currentFiles);
                this.syncAttachFilesForQuestion(target, currentFiles);
                this.saveToLocalStorage(target);
                this.$forceUpdate();

                if (successfulUploads > 0) {
                    this.$notify({
                        type: 'success',
                        text: this.$t('esgPlatform.surveyResponse.fileUploadSuccess')
                    });
                }
            } catch (error) {
                console.error('Error in file upload:', error);
                this.$notify({
                    type: 'error',
                    text: this.$t('esgPlatform.surveyResponse.fileUploadError')
                });
            } finally {
                this.stopLoading();
                target.isUploading = false;
                target.uploadProgress = 0;
            }
        },

        onGetDataFileChange(event, getDataQuestion) {
            const files = Array.from(event.target.files);
            this.handleGetDataFileChange(files, getDataQuestion);
        },

        async handleGetDataDrop(event, getDataQuestion) {
            event.preventDefault();
            this.dragging = false;
            const files = Array.from(event.dataTransfer.files);
            await this.handleGetDataFileChange(files, getDataQuestion);
        },

        async handleActionPlanFileChange(files, plan) {
            console.log('Received file-change:', { files, plan });

            if (!files || !files.length) return;

            if (!plan._id) {
                console.error('Action plan missing _id:', plan);
                this.$notify({
                    type: 'error',
                    text: 'Action plan ID is missing'
                });
                return;
            }

            this.startLoading();
            try {
                plan.isUploading = true;
                const uploadedFiles = [];

                // Upload files to S3
                for (const file of files) {
                    const result = await S3UploadService.singleUpload(file, 'survey-uploads');
                    const fileData = {
                        name: file.name,
                        key: result.key,
                        fileUrl: result.fullPath
                    };
                    uploadedFiles.push(fileData);
                }

                console.log('action plan uploadedFiles', uploadedFiles, plan);

                // Append new files to existing files instead of replacing them
                const existingFiles = plan.files || [];
                const existingUploadedFiles = plan.uploadedFiles || [];

                const newUploadedFiles = uploadedFiles.map(file => ({
                    name: file.name,
                    s3Key: file.key,
                    fileUrl: file.fileUrl,
                    comment: ''
                }));

                // Update plan.files and plan.uploadedFiles reactively by appending
                this.$set(plan, 'files', [...existingFiles, ...uploadedFiles]);
                this.$set(plan, 'uploadedFiles', [...existingUploadedFiles, ...newUploadedFiles]);

                // Find the question
                const questionIndex = this.questions.findIndex(q => q._id === this.currentQuestion._id);
                if (questionIndex === -1) {
                    console.error('Question not found');
                    return;
                }

                let question = this.questions[questionIndex];
                let questionResponse = question._id === this.currentQuestion?._id
                    ? this.activeQuestionResponse
                    : question.response;
                console.log('Question:', question);

                // Create the response object with the required structure
                const response = {
                    id: plan._id,
                    title: plan.title,
                    files: [...existingFiles, ...uploadedFiles], // Include all files (existing + new)
                    isFileRequired: plan.isFileRequired
                };
                console.log('response action plan', response);

                if (question.questionType === 'check-boxes') {
                    // Find the option containing this action plan
                    const selectedOption = question.options.find(opt =>
                        opt.actionPlans.some(ap => ap._id === plan._id)
                    );
                    console.log('Selected option (checkbox):', selectedOption);

                    if (!selectedOption) {
                        console.error('No option found for action plan:', plan._id);
                        return;
                    }

                    // Initialize question.response as an array if it's not
                    if (!Array.isArray(questionResponse)) {
                        console.warn('question.response is not an array, initializing as []');
                        questionResponse = [];
                        if (question._id === this.currentQuestion?._id) {
                            this.activeQuestionResponse = questionResponse;
                        } else {
                            this.$set(question, 'response', questionResponse);
                        }
                    }

                    // Log current responses
                    console.log('Current question.response:', questionResponse);

                    // Find or create the response for this option
                    let responseIndex = questionResponse.findIndex(resp =>
                        resp.value && resp.value.id === selectedOption._id
                    );
                    console.log('Response index:', responseIndex);

                    if (responseIndex === -1) {
                        // No response exists for this option, create one
                        const newResponse = {
                            value: { id: selectedOption._id, title: selectedOption.title },
                            actionPlanResponses: []
                        };
                        questionResponse.push(newResponse);
                        responseIndex = questionResponse.length - 1;
                        console.log('Created new response for option:', newResponse);
                    }

                    // Initialize actionPlanResponses if it doesn't exist
                    if (!questionResponse[responseIndex].actionPlanResponses) {
                        this.$set(questionResponse[responseIndex], 'actionPlanResponses', []);
                    }

                    // Update or add the action plan response
                    const existingIndex = questionResponse[responseIndex].actionPlanResponses.findIndex(
                        resp => resp.id === plan._id
                    );
                    console.log('existingIndex action plan (checkbox):', existingIndex);

                    if (existingIndex !== -1) {
                        this.$set(
                            questionResponse[responseIndex].actionPlanResponses,
                            existingIndex,
                            response
                        );
                    } else {
                        questionResponse[responseIndex].actionPlanResponses.push(response);
                    }

                    // Ensure reactivity
                    this.$set(
                        questionResponse[responseIndex],
                        'actionPlanResponses',
                        [...questionResponse[responseIndex].actionPlanResponses]
                    );

                    // Log updated response
                    console.log('Updated question.response:', questionResponse);
                } else if (question.questionType === 'radio-button') {
                    // Initialize response if it doesn't exist
                    if (!questionResponse) {
                        questionResponse = {};
                        if (question._id === this.currentQuestion?._id) {
                            this.activeQuestionResponse = questionResponse;
                        } else {
                            this.$set(question, 'response', questionResponse);
                        }
                    }
                    if (!questionResponse.actionPlanResponses) {
                        this.$set(questionResponse, 'actionPlanResponses', []);
                    }

                    // Find the option to ensure it's valid
                    const selectedOption = question.options.find(opt =>
                        opt.actionPlans.some(ap => ap._id === plan._id)
                    );
                    console.log('Selected option (radio):', selectedOption);

                    if (!selectedOption) {
                        console.error('No option found for action plan:', plan._id);
                        return;
                    }

                    // Update or add the action plan response
                    const existingIndex = questionResponse.actionPlanResponses.findIndex(
                        resp => resp.id === plan._id
                    );
                    console.log('existingIndex action plan (radio):', existingIndex);

                    if (existingIndex !== -1) {
                        this.$set(questionResponse.actionPlanResponses, existingIndex, response);
                    } else {
                        questionResponse.actionPlanResponses.push(response);
                    }

                    // Ensure reactivity
                    this.$set(questionResponse, 'actionPlanResponses', [...questionResponse.actionPlanResponses]);

                    // Log updated response
                    console.log('Updated question.response:', questionResponse);
                } else {
                    console.error('Unsupported question type:', question.questionType);
                    return;
                }

                // Save to local storage to persist changes
                this.saveToLocalStorage(question);

                this.$notify({
                    type: 'success',
                    text: 'Files uploaded successfully'
                });
            } catch (error) {
                console.error('Error uploading files:', error);
                this.$notify({
                    type: 'error',
                    text: 'Failed to upload files'
                });
            } finally {
                plan.isUploading = false;
                this.stopLoading();
            }
        },

        handleActionPlanFileRemove({ plan, fileIndex }) {
            console.log('handleActionPlanFileRemove called with:', { plan, fileIndex });

            if (!plan || !plan.files || fileIndex < 0 || fileIndex >= plan.files.length) {
                console.error('Invalid parameters for file removal');
                return;
            }

            // Remove the file from the plan's files array
            plan.files.splice(fileIndex, 1);

            // Also remove from uploadedFiles if it exists
            if (plan.uploadedFiles && plan.uploadedFiles.length > fileIndex) {
                plan.uploadedFiles.splice(fileIndex, 1);
            }

            // Find the question and update the response
            const questionIndex = this.questions.findIndex(q => q._id === this.currentQuestion._id);
            if (questionIndex === -1) {
                console.error('Question not found');
                return;
            }

            const question = this.questions[questionIndex];
            const questionResponse = question._id === this.currentQuestion?._id
                ? this.activeQuestionResponse
                : question.response;

            if (question.questionType === 'check-boxes') {
                // Find the option containing this action plan
                const selectedOption = question.options.find(opt =>
                    opt.actionPlans.some(ap => ap._id === plan._id)
                );

                if (selectedOption && questionResponse) {
                    const responseIndex = questionResponse.findIndex(resp =>
                        resp.value && resp.value.id === selectedOption._id
                    );

                    if (responseIndex !== -1 && questionResponse[responseIndex].actionPlanResponses) {
                        const actionPlanResponseIndex = questionResponse[responseIndex].actionPlanResponses.findIndex(
                            resp => resp.id === plan._id
                        );

                        if (actionPlanResponseIndex !== -1) {
                            // Update the files array in the response
                            questionResponse[responseIndex].actionPlanResponses[actionPlanResponseIndex].files = [...plan.files];

                            // If no files left, remove the entire action plan response
                            if (plan.files.length === 0) {
                                questionResponse[responseIndex].actionPlanResponses.splice(actionPlanResponseIndex, 1);
                            }
                        }
                    }
                }
            } else if (question.questionType === 'radio-button') {
                if (questionResponse && questionResponse.actionPlanResponses) {
                    const actionPlanResponseIndex = questionResponse.actionPlanResponses.findIndex(
                        resp => resp.id === plan._id
                    );

                    if (actionPlanResponseIndex !== -1) {
                        // Update the files array in the response
                        questionResponse.actionPlanResponses[actionPlanResponseIndex].files = [...plan.files];

                        // If no files left, remove the entire action plan response
                        if (plan.files.length === 0) {
                            questionResponse.actionPlanResponses.splice(actionPlanResponseIndex, 1);
                        }
                    }
                }
            }

            // Save to local storage to persist changes
            this.saveToLocalStorage(question);

            // Force update to ensure reactivity
            this.$forceUpdate();

            this.$notify({
                type: 'success',
                text: 'File removed successfully'
            });
        },

        async handleGetDataFileChange(files, getDataQuestion) {
            console.log('handleGetDataFileChange', files, getDataQuestion);
            if (!files || files.length === 0) return;
            this.startLoading();
            try {
                const newResponse = [];
                for (const file of files) {
                    try {
                        const result = await S3UploadService.singleUpload(file, 'survey-uploads');
                        newResponse.push({
                            key: result.key,
                            fileName: file.name,
                            fileUrl: result.fullPath
                        });
                    } catch (error) {
                        console.error('Error uploading file:', file.name, error);
                        this.$notify({
                            type: 'error',
                            text: `Failed to upload ${file.name}`
                        });
                    }
                }

                // Get existing files to append new ones
                let existingFiles = [];
                const currentResponse = this.currentQuestionResponse(getDataQuestion);
                
                if (currentResponse) {
                    if (Array.isArray(currentResponse)) {
                        existingFiles = currentResponse;
                    } else {
                        // If it's a single file object, convert to array
                        existingFiles = [currentResponse];
                    }
                }
                
                console.log('existingFiles-->', existingFiles);
                console.log('newResponse-->', newResponse);
                
                // Combine existing files with new files
                const combinedFiles = [...existingFiles, ...newResponse];
                
                // Update the response with combined files
                this.updateGetDataResponse({
                    questionId: getDataQuestion._id,
                    value: combinedFiles,
                    dataType: getDataQuestion.dataType
                });

                // Save to local storage to persist changes
                this.saveToLocalStorage(this.currentQuestion);

                this.$notify({
                    type: 'success',
                    text: this.$t('esgPlatform.surveyResponse.fileUploadSuccess')
                });

            } catch (error) {
                console.error('Error in file upload:', error);
                this.$notify({
                    type: 'error',
                    text: this.$t('esgPlatform.surveyResponse.fileUploadError')
                });
            } finally {
                this.stopLoading();
            }
        },

        removeGetDataFile(index, getDataQuestion) {
            const questionId = getDataQuestion._id;
            
            // Find the response using the same logic as currentQuestionResponse
            const responses = Array.isArray(this.activeQuestionResponse)
                ? this.activeQuestionResponse
                : [];
            const response = responses.find(
                resp => resp?.id === questionId
            );
            
            
            if (response && response.value) {
                if (Array.isArray(response.value)) {
                    response.value.splice(index, 1);
                    if (response.value.length === 0) {
                        response.value = null;
                    }
                } else {
                    response.value = null;
                }
            }

            this.$forceUpdate();
        },

        removeAttachFile(index, question) {
            console.log('removeAttachFile', { index, question });
            const files = [...this.getAttachFilesForQuestion(question)];
            if (index < 0 || index >= files.length) return;
            files.splice(index, 1);
            this.syncAttachFilesForQuestion(question, files);
            this.saveToLocalStorage(question);
            this.$forceUpdate();
        },

        openSignatureDialog(question) {
            console.log('Opening signature dialog for question:', question);
            this.currentSignatureQuestion = question;
            if (question.questionType) {
                this.signatureOptions = question.additionalSetting || {};
            } else if (question.dataType) {
                this.signatureOptions = question.additionalSettings || {};
            }
            this.showSignatureDialog = true;
            console.log('showSignatureDialog set to:', this.showSignatureDialog);
        },
        handleSignature(signatureData) {
            if (!signatureData) return;

            const question = this.currentSignatureQuestion;
            const signatureKey = signatureData.s3key || signatureData.key || signatureData?.value?.s3key || signatureData?.value?.key;
            const signatureAttachmentLink = signatureData.attachmentLink || signatureData?.value?.attachmentLink;

            if (!signatureKey || !signatureAttachmentLink) {
                this.$notify({
                    type: 'warning',
                    text: this.$t('esgPlatform.surveyResponse.signatureRequired')
                });
                this.showSignatureDialog = false;
                return;
            }

            const signatureInfo = {
                attachmentLink: signatureAttachmentLink,
                key: signatureKey
            };

            if (question.questionType === 'digital-signature') {
            // For separate response questions
            if (this.isSeparateResponseQuestion && this.currentFarmId) {
                const farmId = this.currentFarmId;
                const questionId = question._id;
                
                if (!this.farmResponses[farmId]) {
                    this.$set(this.farmResponses, farmId, {});
                }
                
                this.$set(this.farmResponses[farmId], questionId, { 
                    value: signatureInfo,
                    farmIds: [farmId]
                });
            } else {
                    this.activeSignature = {
                        attachmentLink: signatureInfo.attachmentLink,
                        s3key: signatureInfo.key
                    };
                }
            } else if (question.dataType === 'digital-signature') {
                // For get-data signature questions
                this.updateGetDataResponse({
                    questionId: question._id,
                    value: signatureInfo,
                    dataType: question.dataType
                });
            }

            this.showSignatureDialog = false;
        },
        removeSignature(question) {
            console.log('removeSignature', question);

            if (question.questionType === 'digital-signature') {
                // For separate response questions
                if (this.isSeparateResponseQuestion && this.currentFarmId) {
                    const farmId = this.currentFarmId;
                    const questionId = question._id;
                    
                    if (this.farmResponses[farmId] && this.farmResponses[farmId][questionId]) {
                        this.$set(this.farmResponses[farmId], questionId, { value: null, farmIds: [farmId] });
                    }
                } else {
                    this.activeSignature = null;
                }
            } else if (question.dataType === 'digital-signature') {
                // For get-data signature questions
                const questionId = question._id;
                const response = this.activeQuestionResponse?.find?.(q => q.id === questionId);
                if (response) {
                    this.$set(response, 'value', null);
                }
            }

            this.$forceUpdate(); // Ensure UI updates
        },

        // --- Response Creation Methods ---
        createTextResponse(questionId, assessmentId, goalId, value, protocolId, issueId) {
            return {
                esgQuestionId: questionId,
                esgAssessmentId: assessmentId,
                esgGoalId: goalId,
                esgProtocolId: protocolId,
                esgIssueId: issueId,
                submittedByType: 'sub-organization',
                submittedById: null,
                response: {
                    value: value
                }
            };
        },

        createNumericResponse(questionId, assessmentId, goalId, value, protocolId, issueId) {
            return {
                esgQuestionId: questionId,
                esgAssessmentId: assessmentId,
                esgGoalId: goalId,
                esgProtocolId: protocolId,
                esgIssueId: issueId,
                submittedByType: 'sub-organization',
                submittedById: null,
                response: {
                    value: Number(value)
                }
            };
        },

        createDateResponse(questionId, assessmentId, goalId, dateValue, protocolId, issueId) {
            return {
                esgQuestionId: questionId,
                esgAssessmentId: assessmentId,
                esgGoalId: goalId,
                esgProtocolId: protocolId,
                esgIssueId: issueId,
                submittedByType: 'sub-organization',
                submittedById: null,
                response: {
                    value: dateValue
                }
            };
        },

        createSignatureResponse(questionId, assessmentId, goalId, signature, protocolId, issueId) {
            const signatureKey = signature?.s3key || signature?.key || signature?.value?.s3key || signature?.value?.key;
            const signatureAttachmentLink = signature?.attachmentLink || signature?.value?.attachmentLink || signature?.fileUrl || signature?.s3Location;
            return {
                esgQuestionId: questionId,
                esgAssessmentId: assessmentId,
                esgGoalId: goalId,
                esgProtocolId: protocolId,
                esgIssueId: issueId,
                submittedByType: 'sub-organization',
                submittedById: null,
                response: {
                    value: signatureKey && signatureAttachmentLink
                        ? {
                            key: signatureKey,
                            attachmentLink: signatureAttachmentLink
                        }
                        : null
                }
            };
        },

        createAttachmentResponse(questionId, assessmentId, goalId, uploadedFiles, protocolId, issueId) {
            return {
                esgQuestionId: questionId,
                esgAssessmentId: assessmentId,
                esgGoalId: goalId,
                esgProtocolId: protocolId,
                esgIssueId: issueId,
                submittedByType: 'sub-organization',
                submittedById: null,
                response: {
                    value: this.mapAttachFilesToResponseValue(uploadedFiles)
                }
            };
        },

        // Helper function to format measurement unit response
        formatMeasurementUnitResponse(gdq) {
            console.log('formatMeasurementUnitResponse', gdq);
            const response = {
                id: gdq._id || gdq.id,
                title: gdq.title,
                dataType: gdq.dataType,
                value: gdq.value || null
            };
            console.log('response>>>>>', response);
            return response;
        },

        createRadioResponse(questionId, assessmentId, goalId, option, actionPlanResponses = [], getDataResponses = [], protocolId, issueId) {
            return {
                esgQuestionId: questionId,
                esgAssessmentId: assessmentId,
                esgGoalId: goalId,
                esgProtocolId: protocolId,
                esgIssueId: issueId,
                submittedByType: 'sub-organization',
                submittedById: null,
                response: {
                    value: {
                        id: option._id,
                        label: option.label,
                        isActionPlanEnabled: option.isActionPlanEnabled,
                        isGetDataEnabled: option.isGetDataEnabled,
                        isRecommendatationEnabled: option.isRecommendatationEnabled,
                        isDimitraModulesEnabled: option.isDimitraModulesEnabled
                    },
                    actionPlanResponses: (actionPlanResponses || []).map(plan => ({
                        id: plan.id,
                        title: plan.title,
                        isFileRequired: plan.isFileRequired,
                        files: plan.files
                    })),
                    getDataResponses: (getDataResponses || []).map(gdq => {
                        let response;
                        console.log('radio gdq>>>>>', gdq);
                        switch (gdq.dataType) {
                            case 'text-field':
                            case 'text-area':
                                response = {
                                    id: gdq.id,
                                    title: gdq.title,
                                    dataType: gdq.dataType,
                                    value: gdq.value || null
                                };
                                break;
                            case 'numeric':
                                response = {
                                    id: gdq.id,
                                    title: gdq.title,
                                    dataType: gdq.dataType,
                                    value: gdq.value ? Number(gdq.value) : null
                                };
                                break;
                            case 'attach-file':
                                response = {
                                    id: gdq.id,
                                    title: gdq.title,
                                    dataType: gdq.dataType,
                                    value: gdq?.value && gdq?.value[0] || gdq?.response && gdq?.response[0] ? {
                                        key: gdq?.value[0]?.key || gdq?.response[0]?.key,
                                        fileName: gdq?.value[0]?.fileName || gdq?.response[0]?.fileName,
                                        fileUrl: gdq?.value[0]?.fileUrl || gdq?.response[0]?.fileUrl
                                    } : null
                                };
                                break;
                            case 'digital-signature':
                            {
                                const signatureKey = gdq.signature?.s3key || gdq.signature?.key || gdq.value?.s3key || gdq.value?.key;
                                const signatureAttachmentLink = gdq.signature?.attachmentLink || gdq.value?.attachmentLink;
                                response = {
                                    id: gdq.id,
                                    title: gdq.title,
                                    dataType: gdq.dataType,
                                    value: signatureKey && signatureAttachmentLink
                                    ? {key: signatureKey,attachmentLink: signatureAttachmentLink 
                                    } : null
                                };
                            }
                                break;
                            case 'radio-button':
                                response = {
                                    id: gdq.id,
                                    title: gdq.title,
                                    dataType: gdq.dataType,
                                    value: gdq.value || null
                                };
                                break;
                            case 'check-boxes':
                                response = {
                                    id: gdq.id,
                                    title: gdq.title,
                                    dataType: gdq.dataType,
                                    value: Array.isArray(gdq.response || gdq.value) ? gdq.response || gdq.value : []
                                };
                                break;
                            case 'measurement-unit':
                                response = this.formatMeasurementUnitResponse(gdq);
                                break;
                            default:
                                response = {
                                    id: gdq.id,
                                    title: gdq.title,
                                    dataType: gdq.dataType,
                                    value: gdq.value || null
                                };
                        }
                        return response;
                    })
                }
            };
        },

        createCheckboxResponse(questionId, assessmentId, goalId, options, protocolId, issueId) {
            return {
                esgQuestionId: questionId,
                esgAssessmentId: assessmentId,
                esgGoalId: goalId,
                esgProtocolId: protocolId,
                esgIssueId: issueId,
                submittedByType: 'sub-organization',
                submittedById: null,
                response: options?.map(option => ({
                    value: {
                        id: option.value.id,
                        label: option.value.label,
                        isActionPlanEnabled: option.value.isActionPlanEnabled,
                        isGetDataEnabled: option.value.isGetDataEnabled,
                        isRecommendatationEnabled: option.value.isRecommendatationEnabled,
                        isDimitraModulesEnabled: option.value.isDimitraModulesEnabled
                    },
                    actionPlanResponses: (option.actionPlanResponses || []).map(plan => ({
                        id: plan.id || plan._id, // Ensure id is included
                        title: plan.title,
                        files: (plan.files || []).map(file => ({
                            key: file.key,
                            name: file.name,
                            fileUrl: file.fileUrl
                        })),
                        isFileRequired: plan.isFileRequired
                    })),
                    getDataResponses: (option.getDataResponses || []).map(gdq => {
                        console.log('checkboxes gdq>>>>>', gdq);
                        let response;
                        switch (gdq.dataType) {
                            case 'text-field':
                            case 'text-area':
                                response = {
                                    id: gdq.id,
                                    title: gdq.title,
                                    dataType: gdq.dataType,
                                    value: gdq.value
                                };
                                break;
                            case 'numeric':
                                response = {
                                    id: gdq.id,
                                    title: gdq.title,
                                    dataType: gdq.dataType,
                                    value: Number(gdq.value)
                                };
                                break;
                            case 'attach-file':
                                response = {
                                    id: gdq.id,
                                    title: gdq.title,
                                    dataType: gdq.dataType,
                                    value: gdq?.value && gdq?.value[0] || gdq?.response && gdq?.response[0] ? {
                                        key: gdq?.value[0]?.key || gdq?.response[0]?.key,
                                        name: gdq?.value[0]?.fileName || gdq?.response[0]?.fileName,
                                        fileUrl: gdq?.value[0]?.fileUrl || gdq?.response[0]?.fileUrl
                                    } : null
                                };
                                break;
                            case 'digital-signature':
                            {
                                const signatureKey = gdq.signature?.s3key || gdq.signature?.key || gdq.value?.s3key || gdq.value?.key;
                                const signatureAttachmentLink = gdq.signature?.attachmentLink || gdq.value?.attachmentLink;
                                response = {
                                    id: gdq.id,
                                    title: gdq.title,
                                    dataType: gdq.dataType,
                                    value: signatureKey && signatureAttachmentLink
                                    ? {
                                        key: signatureKey,
                                        attachmentLink: signatureAttachmentLink
                                    } : null
                                };
                            }
                                break;
                            case 'radio-button':
                                response = {
                                    id: gdq.id,
                                    title: gdq.title,
                                    dataType: gdq.dataType,
                                    value: gdq.value || null
                                };
                                break;
                            case 'check-boxes':
                                response = {
                                    id: gdq.id,
                                    title: gdq.title,
                                    dataType: gdq.dataType,
                                    value: Array.isArray(gdq.response || gdq.value) ? gdq.response || gdq.value : []
                                };
                                break;
                            case 'measurement-unit':
                                response = this.formatMeasurementUnitResponse(gdq);
                                break;
                            default:
                                response = {
                                    id: gdq.id,
                                    title: gdq.title,
                                    dataType: gdq.dataType,
                                    value: gdq.value
                                };
                        }
                        return response;
                    })
                }))
            };
        },

        createGetDataResponse(questionId, assessmentId, goalId, responses, protocolId, issueId) {
            return {
                submittedByType: 'sub-organization',
                submittedById: null,
                esgQuestionId: questionId,
                esgAssessmentId: assessmentId,
                esgGoalId: goalId,
                esgProtocolId: protocolId,
                esgIssueId: issueId,
                // nextStep,
                response: responses?.length ? responses?.map(response => {
                    if (response && response.dataType === 'attach-file') {
                        return {
                            id: response.id,
                            title: response.title,
                            dataType: response.dataType,
                            value: response.value?.map(file => ({ key: file.key, fileName: file.fileName, fileUrl: file.fileUrl })),
                        };
                    } if (response && response.dataType === 'measurement-unit') {
                        return response;
                    }
                    if (response && response.dataType === 'digital-signature') {
                        const signatureKey = response.value?.key || response.value?.s3key;
                        const signatureAttachmentLink = response.value?.attachmentLink || response.value?.fileUrl || response.value?.s3Location;
                        return {
                            id: response.id,
                            title: response.title,
                            dataType: response.dataType,
                            value: signatureKey && signatureAttachmentLink
                                ? { attachmentLink: signatureAttachmentLink, key: signatureKey }
                                : null,
                        };
                    }

                    if (response && response.dataType === 'check-boxes') {
                        return { id: response.id, title: response.title, dataType: response.dataType, value: response.value };
                    }

                    if (response && response.dataType === 'radio-button') {
                        return { id: response.id, title: response.title, dataType: response.dataType, value: response.value };
                    }

                    if (response && (response.dataType === 'text-field' || response.dataType === 'text-area' || response.dataType === 'numeric' || response.dataType === 'date')) {
                        return { id: response.id, title: response.title, dataType: response.dataType, value: response.value };
                    }

                }) : null,
            };
        },

        scrollToFirstError() {
            this.$nextTick(() => {
                const errorField = document.querySelector('.v-input--has-state .v-messages__message, .red--text.text-caption');
                if (errorField) {
                    errorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            });
        },
        attachRepeatMetadata(payload) {
            if (!this.hasRepeatSubmissions || !this.selectedRepetitionDate) return payload;
            return { ...payload, repetitionDate: this.selectedRepetitionDate };
        },
        updateRepeatResponsesAfterSubmit(resultResponse, payload) {
            const question = this.currentQuestion;
            if (!question || !this.selectedRepetitionDate) return;

            const responseBody = this.cloneRepeatValue(
                this.extractAssessmentResponse(resultResponse) || payload?.response || null
            );
            const submittedOn = this.getRepeatResponseSubmittedOn(resultResponse) || new Date().toISOString();
            const newEntry = {
                repetitionDate: this.selectedRepetitionDate,
                assessmentResponse: responseBody,
                rejectionReason: resultResponse?.rejectionReason ?? null,
                submittedOn
            };

            if (!Array.isArray(question._repeatResponses)) {
                this.$set(question, '_repeatResponses', []);
            }

            while (question._repeatResponses.length <= this.selectedDateIndex) {
                question._repeatResponses.push(null);
            }
            this.$set(question._repeatResponses, this.selectedDateIndex, newEntry);
            this.clearRepeatDraft(question, this.selectedDateIndex);

            this.applyRepeatResponseForSelectedDate();
        },

        // --- Submission Methods ---
        async submitSurvey() {
            if (this.hasRepeatSubmissions) {
                return this.submitEntireSurvey();
            }
            return this.submitCurrentQuestionResponse(false);
        },
        async submitCurrentQuestionResponse(useRepeatContext = false) {
            if (useRepeatContext && !this.isActiveRepeatWindow) {
                this.$notify({
                    type: 'warning',
                    text: 'Submission is only allowed for the current active window.'
                });
                return;
            }
            // Validate standard fields
            const isValid = this.$refs.surveyForm?.validate();
            // Validate custom fields
            const filesValid = this.validateFiles();
            const signaturesValid = this.validateSignatures();

            if (!isValid || !filesValid || !signaturesValid) {
                this.scrollToFirstError();
                return;
            }

            try {
                this.startLoading();
                const currentQuestion = this.questions[this.currentQuestionIndex];
                if (!currentQuestion) throw new Error('No question found');
                const questionResponse = this.activeQuestionResponse;
                const uploadedFiles = this.activeUploadedFiles;
                const signature = this.activeSignature;

                // Validate file uploads
                if (currentQuestion.getDataQuestions?.length > 0) {
                    for (const gdq of currentQuestion.getDataQuestions) {
                        if (gdq.dataType === 'attach-file' && gdq.isMandatory && gdq.uploadedFiles?.length === 0 && gdq.isUploading) {
                            this.$notify({ type: 'warning', text: this.$t('esgPlatform.surveyResponse.waitForFileUpload') });
                            return;
                        }
                    }
                }
                if (currentQuestion.questionType === 'radio-button' && this.selectedActionPlans?.length > 0) {
                    for (const actionPlan of this.selectedActionPlans) {
                        if (actionPlan.isFileRequired && actionPlan.uploadedFiles?.length === 0 && actionPlan.isUploading) {
                            this.$notify({ type: 'warning', text: this.$t('esgPlatform.surveyResponse.waitForFileUpload') });
                            return;
                        }
                    }
                }


                // Create response based on question type
                let response;
                let selectedOption;
                let selectedOptions;

                console.log(currentQuestion.questionType);
                switch (currentQuestion.questionType) {
                    case 'text-field':
                    case 'text-area':
                        response = this.createTextResponse(
                            currentQuestion._id,
                            this.currentAssessmentId,
                            this.currentGoalId,
                            questionResponse,
                            this.esgProtocolId,
                            this.esgIssueId,
                        );
                        break;

                    case 'numeric':
                        response = this.createNumericResponse(
                            currentQuestion._id,
                            this.currentAssessmentId,
                            this.currentGoalId,
                            questionResponse,
                            this.esgProtocolId,
                            this.esgIssueId,
                        );
                        break;

                    case 'date':
                        response = this.createDateResponse(
                            currentQuestion._id,
                            this.currentAssessmentId,
                            this.currentGoalId,
                            questionResponse,
                            this.esgProtocolId,
                            this.esgIssueId,
                        );
                        break;

                    case 'digital-signature':
                        response = this.createSignatureResponse(
                            currentQuestion._id,
                            this.currentAssessmentId,
                            this.currentGoalId,
                            signature,
                            this.esgProtocolId,
                            this.esgIssueId,
                        );
                        break;

                    case 'radio-button': {
                        const currentQuestion = this.questions.find(q => q._id === this.currentQuestion._id);
                        selectedOption = currentQuestion.options.find(opt =>
                            opt._id === questionResponse?.value
                        );
                        if (!selectedOption) {
                            if (currentQuestion.isMandatory) {
                                this.$notify({
                                    type: 'error',
                                    text: this.$t('esgPlatform.surveyResponse.selectedOptionNotFound')
                                });
                                return;
                            }
                            break;
                        }
                        response = this.createRadioResponse(
                            currentQuestion._id,
                            this.currentAssessmentId,
                            this.currentGoalId,
                            selectedOption,
                            questionResponse?.actionPlanResponses || [],
                            questionResponse?.getDataResponses || [],
                            this.esgProtocolId,
                            this.esgIssueId
                        );
                        break;
                    }

                    case 'check-boxes': {
                        selectedOptions = Array.isArray(questionResponse)
                            ? questionResponse
                            : (questionResponse ? [questionResponse] : []);
                        response = this.createCheckboxResponse(
                            currentQuestion._id,
                            this.currentAssessmentId,
                            this.currentGoalId,
                            selectedOptions,
                            this.esgProtocolId,
                            this.esgIssueId,
                        );
                        break;
                    }

                    case 'attach-files': {
                        if (!uploadedFiles?.length) {
                            if (currentQuestion.isMandatory) {
                                this.$notify({
                                    type: 'error',
                                    text: this.$t('esgPlatform.surveyResponse.fileRequired')
                                });
                                return;
                            }
                        }
                        response = this.createAttachmentResponse(
                            currentQuestion._id,
                            this.currentAssessmentId,
                            this.currentGoalId,
                            uploadedFiles || [],
                            this.esgProtocolId,
                            this.esgIssueId,
                        );
                        break;
                    }

                    default: {
                        if (currentQuestion.getDataQuestions?.length > 0) {
                            response = this.createGetDataResponse(
                                currentQuestion._id,
                                this.currentAssessmentId,
                                this.currentGoalId,
                                questionResponse,
                                this.esgProtocolId,
                                this.esgIssueId,
                            );
                        } else {
                            this.$notify({
                                type: 'error',
                                text: `Unsupported question type: ${currentQuestion.questionType}`
                            });
                            return;
                        }
                    }
                }


                if (!response) {
                    this.$notify({ type: 'error', text: 'Failed to create response data' });
                    return;
                }

                const payload = useRepeatContext ? this.attachRepeatMetadata(response) : response;
                const result = await EsgService.submitSurveyResponse(payload);
                if (useRepeatContext) {
                    this.updateRepeatResponsesAfterSubmit(result?.response, payload);
                } else if (result?.response) {
                    currentQuestion._originalResponse = result.response;
                    currentQuestion.response = currentQuestion.questionType === 'get-data'
                        ? questionResponse
                        : result.response.value;
                } else if (currentQuestion._originalResponse === undefined) {
                    currentQuestion._originalResponse = currentQuestion.response;
                }

                this.$notify({
                    type: 'success',
                    text: this.$t('esgPlatform.surveyResponse.surveySubmittedSuccess')
                });

            } catch (error) {
                console.error('Error submitting survey:', error);
                this.$notify({
                    type: 'error',
                    text: this.$t('esgPlatform.surveyResponse.errorSubmittingSurvey')
                });
            } finally {
                this.stopLoading();
            }
        },


        async submitEntireSurvey() {
            if (this.hasRepeatSubmissions) {
                return this.submitCurrentQuestionResponse(true);
            }
            try {
                this.startLoading();

                // First save the current question to local storage
                if (this.currentQuestion) {
                    this.saveToLocalStorage(this.currentQuestion);
                }

                // Wait for localStorage to be updated
                await new Promise(resolve => setTimeout(resolve, 1000));

                // Load responses from local storage
                const key = `survey_responses_${this.surveyId}_${this.goalId}`;
                const savedResponses = JSON.parse(localStorage.getItem(key)) || {};

                // Verify localStorage data
                if (Object.keys(savedResponses).length === 0) {
                    this.$notify({
                        type: 'warning',
                        text: this.$t('esgPlatform.surveyResponse.noResponsesToSubmit')
                    });
                    return;
                }

                const responses = [];

                // Process only the questions that have responses in localStorage
                for (const [questionId, savedResponse] of Object.entries(savedResponses)) {
                    const question = this.questions.find(q => q._id === questionId);
                    if (!question) continue;

                    let selectedOption;

                    switch (question.questionType) {
                        case 'text-field':
                        case 'text-area':
                            responses.push({
                                esgQuestionId: question._id,
                                esgAssessmentId: this.currentAssessmentId,
                                esgGoalId: this.currentGoalId,
                                esgProtocolId: this.esgProtocolId,
                                esgIssueId: this.esgIssueId,
                                submittedByType: 'sub-organization',
                                submittedById: null,
                                response: {
                                    value: savedResponse
                                }
                            });
                            break;

                        case 'numeric':
                            responses.push({
                                esgQuestionId: question._id,
                                esgAssessmentId: this.currentAssessmentId,
                                esgGoalId: this.currentGoalId,
                                esgProtocolId: this.esgProtocolId,
                                esgIssueId: this.esgIssueId,
                                submittedByType: 'sub-organization',
                                submittedById: null,
                                response: {
                                    value: Number(savedResponse)
                                }
                            });
                            break;

                        case 'date':
                            responses.push({
                                esgQuestionId: question._id,
                                esgAssessmentId: this.currentAssessmentId,
                                esgGoalId: this.currentGoalId,
                                esgProtocolId: this.esgProtocolId,
                                esgIssueId: this.esgIssueId,
                                submittedByType: 'sub-organization',
                                submittedById: null,
                                response: {
                                    value: savedResponse
                                }
                            });
                            break;

                        case 'digital-signature':
                            {
                                const signatureKey = savedResponse?.value?.key || savedResponse?.value?.s3key;
                                const signatureAttachmentLink = savedResponse?.value?.attachmentLink || savedResponse?.value?.fileUrl || savedResponse?.value?.s3Location;
                                if (signatureKey && signatureAttachmentLink) {
                                responses.push({
                                    esgQuestionId: question._id,
                                    esgAssessmentId: this.currentAssessmentId,
                                    esgGoalId: this.currentGoalId,
                                    esgProtocolId: this.esgProtocolId,
                                    esgIssueId: this.esgIssueId,
                                    submittedByType: 'sub-organization',
                                    submittedById: null,
                                    response: {
                                        value: {
                                            attachmentLink: signatureAttachmentLink,
                                            name: savedResponse.value?.fileName,
                                            key: signatureKey
                                        }
                                    }
                                });
                            }
                            }
                            break;

                        case 'attach-files':
                            if (savedResponse?.value?.length > 0) {
                                responses.push({
                                    esgQuestionId: question._id,
                                    esgAssessmentId: this.currentAssessmentId,
                                    esgGoalId: this.currentGoalId,
                                    esgProtocolId: this.esgProtocolId,
                                    esgIssueId: this.esgIssueId,
                                    submittedByType: 'sub-organization',
                                    submittedById: null,
                                    response: {
                                        value: question.uploadedFiles.map(file => ({
                                            key: file?.s3Key,
                                            comment: file?.comment || '',
                                            fileName: file?.fileName,
                                            fileUrl: file?.fileUrl || file?.s3Location || null
                                        }))
                                    }
                                });
                            }
                            break;

                        case 'radio-button': {
                            selectedOption = question.options.find(opt =>
                                opt._id === savedResponse?.value
                            );
                            if (selectedOption) {
                                responses.push(this.createRadioResponse(
                                    question._id,
                                    this.currentAssessmentId,
                                    this.currentGoalId,
                                    selectedOption,
                                    savedResponse?.actionPlanResponses || [],
                                    savedResponse?.getDataResponses || [],
                                    this.esgProtocolId,
                                    this.esgIssueId
                                ));
                            }
                            break;
                        }

                        case 'check-boxes': {
                            if (Array.isArray(savedResponse)) {
                                responses.push(this.createCheckboxResponse(
                                    question._id,
                                    this.currentAssessmentId,
                                    this.currentGoalId,
                                    savedResponse,
                                    this.esgProtocolId,
                                    this.esgIssueId
                                ));
                            }
                            break;
                        }

                        case 'get-data': {
                            if (question.getDataQuestions?.length > 0) {
                                responses.push(this.createGetDataResponse(
                                    question._id,
                                    this.currentAssessmentId,
                                    this.currentGoalId,
                                    savedResponse,
                                    this.esgProtocolId,
                                    this.esgIssueId
                                ));
                            }
                            break;
                        }
                    }
                }

                // Submit all responses at once
                if (responses.length > 0) {
                    await EsgService.submitSurveyResponse(responses);
                    this.$notify({
                        type: 'success',
                        text: this.$t('esgPlatform.surveyResponse.surveyCompletedSuccess')
                    });
                    // Clear local storage after successful submission
                    localStorage.removeItem(key);
                    this.goToSurveyOverview();
                } else {
                    this.$notify({
                        type: 'warning',
                        text: this.$t('esgPlatform.surveyResponse.noResponsesToSubmit')
                    });
                }
            } catch (error) {
                console.error('Error submitting entire survey:', error);
                this.$notify({
                    type: 'error',
                    text: this.$t('esgPlatform.surveyResponse.errorSubmittingSurvey')
                });
            } finally {
                this.stopLoading();
            }
        },
        // --- Utility Methods ---

        handleConfirm() {
            if (this.confirmAction) this.confirmAction();
            this.openConfirmBox = false;
            this.confirmAction = null;
        },

        getFileExtension(doc) {
            console.log('doc11>>>>', doc);
            const parts = doc.name?.split('.') || doc.fileName?.split('.');
            return parts.length > 1 ? parts.pop().toUpperCase() : '';
        },

        getFileName(doc) {
            return doc?.name || doc?.fileName || 'Unnamed file';
        },

        isOptionSelected(option) {
            const responseSource = this.isSeparateResponseQuestion
                ? this.currentFarmResponse
                : this.activeQuestionResponse;

            if (!Array.isArray(responseSource)) {
                return !!option?.selected;
            }

            return responseSource.some(resp => {
                const responseValue = resp?.value || {};
                return responseValue?.id === option?._id || responseValue?.label === option?.label;
            });
        },

        handleCheckboxChange(option, isChecked) {
            if (!option || !option._id) return;
            const nextSelected = typeof isChecked === 'boolean'
                ? isChecked
                : !this.isOptionSelected(option);
            this.$set(option, 'selected', nextSelected);

        if (!this.isSeparateResponseQuestion) {
            if (!Array.isArray(this.activeQuestionResponse)) {
                this.activeQuestionResponse = [];
            }

            const questionResponse = this.activeQuestionResponse;
            const isSelected = nextSelected;
            const existingResponseIndex = questionResponse.findIndex(
                resp => resp?.value?.id === option._id
            );

            this.$set(option, 'showDetails', isSelected);

            if (isSelected) {
                if (existingResponseIndex === -1) {
                    const newResponse = {
                        value: {
                            id: option._id,
                            label: option.label,
                            isActionPlanEnabled: option.isActionPlanEnabled,
                            isGetDataEnabled: option.isGetDataEnabled,
                            isDimitraModulesEnabled: option.isDimitraModulesEnabled
                        },
                        actionPlanResponses: [],
                        getDataResponses: [],
                    };

                    if (option.isActionPlanEnabled && option.actionPlans) {
                        option.actionPlans = option.actionPlans.map(plan => ({
                            ...plan,
                            files: [],
                            uploadedFiles: [],
                            isUploading: false
                        }));
                    }

                        questionResponse.push(newResponse);
                    }
                } else if (existingResponseIndex !== -1) {
                    questionResponse.splice(existingResponseIndex, 1);
                }
            } else {
                const farmId = this.currentFarmId;
                const questionId = this.currentQuestion._id;
                
                if (!this.farmResponses[farmId]) {
                    this.$set(this.farmResponses, farmId, {});
                }
                
                if (!this.farmResponses[farmId][questionId]) {
                    this.$set(this.farmResponses[farmId], questionId, []);
                }
                
                let farmQuestionResponses = this.farmResponses[farmId][questionId];
                if (!Array.isArray(farmQuestionResponses)) {
                    farmQuestionResponses = [];
                    this.$set(this.farmResponses[farmId], questionId, farmQuestionResponses);
                }
                
                const isSelected = nextSelected;
                const existingResponseIndex = farmQuestionResponses.findIndex(
                    resp => resp.value?.id === option._id
                );
                this.$set(option, 'showDetails', isSelected);
                if (isSelected) {
                    if (existingResponseIndex === -1) {
                        const newResponse = {
                            value: {
                                id: option._id,
                                label: option.label,
                                isActionPlanEnabled: option.isActionPlanEnabled,
                                isGetDataEnabled: option.isGetDataEnabled,
                                isDimitraModulesEnabled: option.isDimitraModulesEnabled
                            },
                            actionPlanResponses: [],
                            getDataResponses: [],
                            farmIds: [farmId]
                        };

                        // Initialize action plans if enabled
                        if (option.isActionPlanEnabled && option.actionPlans) {
                            option.actionPlans = option.actionPlans.map(plan => ({
                                ...plan,
                                files: [],
                                uploadedFiles: [],
                                isUploading: false
                            }));
                        }
                        farmQuestionResponses.push(newResponse);
                    }
                } else {
                    if (existingResponseIndex !== -1) {
                        farmQuestionResponses.splice(existingResponseIndex, 1);
                    }
                }
            }

            this.$forceUpdate();
        },
        handleGetDataCheckboxChange(opt, getDataQuestion) {
            if (!getDataQuestion.response) {
                this.$set(getDataQuestion, 'response', []);
            }

            if (opt.selected) {
                if (!getDataQuestion.response.includes(opt.label)) {
                    getDataQuestion.response.push(opt.label);
                }
            } else {
                getDataQuestion.response = getDataQuestion.response.filter(label => label !== opt.label);
            }
        },
        onRadioChange(selectedValue) {
            const optionIndex = this.currentQuestion.options.findIndex(opt => opt._id == selectedValue);
            if (optionIndex !== -1) {
                const selected = { ...this.currentQuestion.options[optionIndex] };
                if (selected.isGetDataEnabled && selected.getDataQuestions) {
                    selected.getDataQuestions = selected.getDataQuestions.map(q => ({
                        ...q,
                        response: null,
                        files: [],
                        uploadedFiles: []
                    }));
                }
                if (selected.isActionPlanEnabled && selected.actionPlans) {


                    selected.actionPlans = selected.actionPlans.map(plan => ({
                        ...plan,
                        files: [],
                        uploadedFiles: [],
                        isUploading: false
                    }));

                    console.log('selected.actionPlans', selected.actionPlans);
                }

                this.$set(this.currentQuestion.options, optionIndex, selected);
                this.activeQuestionResponse = {
                    value: selected?._id,
                    getDataResponses: [],
                    actionPlanResponses: []
                };
            }
            this.$forceUpdate();
        },
        updateGetDataResponse({ questionId, value, dataType }) {
            console.log('updateGetDataResponse', questionId, value, dataType);
            const question = this.currentQuestion;
            if (!question) return;
            let questionResponse = this.activeQuestionResponse;
            let normalizedValue = value;
            const isSameValue = (currentValue, nextValue) =>
                JSON.stringify(currentValue) === JSON.stringify(nextValue);

            if (dataType === 'digital-signature' && value && typeof value === 'object' && !Array.isArray(value)) {
                const signatureKey = value.key || value.s3key || value?.value?.key || value?.value?.s3key;
                const signatureAttachmentLink = value.attachmentLink || value?.value?.attachmentLink;
                normalizedValue = signatureKey && signatureAttachmentLink
                    ? { key: signatureKey, attachmentLink: signatureAttachmentLink }
                    : null;
            }

            if (question.questionType === 'check-boxes') {
                const selectedOption = question.options.find(opt =>
                    opt.getDataQuestions.some(gdq => gdq._id === questionId)
                );
                if (!selectedOption) return;
                if (!Array.isArray(questionResponse)) return;

                const responseIndex = questionResponse.findIndex(resp =>
                    resp.value.id === selectedOption._id
                );
                if (responseIndex === -1) return;

                if (!questionResponse[responseIndex].getDataResponses) {
                    this.$set(questionResponse[responseIndex], 'getDataResponses', []);
                }

                const existingIndex = questionResponse[responseIndex].getDataResponses
                    .findIndex(response => response.id === questionId);

                const response = {
                    id: questionId,
                    title: selectedOption.getDataQuestions.find(q => q._id === questionId)?.title || '',
                    dataType: dataType,
                    value: normalizedValue
                };

                if (existingIndex !== -1) {
                    if (isSameValue(
                        questionResponse[responseIndex].getDataResponses[existingIndex]?.value,
                        response.value
                    )) {
                        return;
                    }
                    this.$set(
                        questionResponse[responseIndex].getDataResponses,
                        existingIndex,
                        response
                    );
                } else {
                    questionResponse[responseIndex].getDataResponses.push(response);
                }

                this.$set(
                    questionResponse[responseIndex],
                    'getDataResponses',
                    [...questionResponse[responseIndex].getDataResponses]
                );
            } else if (question.questionType === 'radio-button') {
                if (!questionResponse) {
                    questionResponse = { value: null, getDataResponses: [], actionPlanResponses: [] };
                    this.activeQuestionResponse = questionResponse;
                }
                if (!questionResponse.getDataResponses) {
                    this.$set(questionResponse, 'getDataResponses', []);
                }

                console.log('question.response.getDataResponses', questionResponse.getDataResponses, questionId);

                const existingIndex = questionResponse.getDataResponses
                    .findIndex(response => response.id === questionId);

                const response = {
                    id: questionId,
                    title: this.selectedRadioOption.getDataQuestions.find(q => q._id === questionId)?.title || '',
                    dataType: dataType,
                    value: normalizedValue
                };

                console.log('response updateGetDataResponse', response, questionId, dataType, value, existingIndex);

                if (existingIndex !== -1) {
                    if (isSameValue(
                        questionResponse.getDataResponses[existingIndex]?.value,
                        response.value
                    )) {
                        return;
                    }
                    this.$set(questionResponse.getDataResponses, existingIndex, response);
                } else {
                    questionResponse.getDataResponses.push(response);
                }

                this.$set(
                    questionResponse,
                    'getDataResponses',
                    [...questionResponse.getDataResponses]
                );
            } else if (question.questionType === 'get-data') {
                console.log('this.currentQuestion.response step 3', question, questionResponse);
                if (!Array.isArray(questionResponse)) {
                    questionResponse = [];
                    this.activeQuestionResponse = questionResponse;
                }
                const existingIndex = questionResponse.findIndex(resp => resp && resp.id === questionId);

                console.log('existingIndex', existingIndex, questionResponse, questionId);
                const response = {
                    id: questionId,
                    title: question.getDataQuestions.find(q => q._id === questionId)?.title || '',
                    dataType: dataType,
                    value: normalizedValue
                };

                console.log('response updateGetDataResponse', response, questionId, dataType, value);

                if (existingIndex !== -1) {
                    if (isSameValue(questionResponse[existingIndex]?.value, response.value)) {
                        return;
                    }
                    this.$set(questionResponse, existingIndex, response);
                } else {
                    questionResponse.push(response);
                }
            }

            this.$forceUpdate();

            console.log('Updated question:', {
                questionType: question.questionType,
                response: questionResponse,
                getDataResponses: question.questionType === 'check-boxes'
                    ? questionResponse.map(r => r.getDataResponses)
                    : questionResponse?.getDataResponses
            });
        },
        validateFiles() {
            this.fileErrors = {};
            let isValid = true;

            if (this.currentQuestion.questionType === 'attach-files' && this.currentQuestion.isMandatory) {
                if (!this.activeUploadedFiles?.length && !this.activeQuestionResponse?.value?.length) {
                    this.$set(this.fileErrors, this.currentQuestion._id, this.$t('esgPlatform.surveyResponse.fileRequired'));
                    isValid = false;
                }
            }

            if (this.currentQuestion.getDataQuestions) {
                for (const gdq of this.currentQuestion.getDataQuestions) {
                    if (gdq.dataType === 'attach-file' && gdq.isMandatory) {
                        const response = this.currentQuestionResponse(gdq);
                        if (!response || !response.length) {
                            this.$set(this.fileErrors, gdq._id, this.$t('esgPlatform.surveyResponse.fileRequired'));
                            isValid = false;
                        }
                    }
                }
            }

            return isValid;
        },
        validateSignatures() {
            this.signatureErrors = {};
            let isValid = true;

            // Check get-data questions
            if (this.currentQuestion.getDataQuestions) {
                for (const gdq of this.currentQuestion.getDataQuestions) {
                    if (gdq.dataType === 'digital-signature' && gdq.isMandatory) {
                        const response = this.currentQuestionResponse(gdq);
                        if (!response || !(response.key || response.s3key)) {
                            this.$set(this.signatureErrors, gdq._id, this.$t('esgPlatform.surveyResponse.signatureRequired'));
                            isValid = false;
                        }
                    }
                }
            }

            // Check main question if it's a signature type
            if (this.currentQuestion.questionType === 'digital-signature' && this.currentQuestion.isMandatory) {
                if (!(this.activeSignature?.key || this.activeSignature?.s3key)) {
                    this.$set(this.signatureErrors, this.currentQuestion._id, this.$t('esgPlatform.surveyResponse.signatureRequired'));
                    isValid = false;
                }
            }

            return isValid;
        },
        getMeasurementValue(getDataQuestion) {
            const resp = this.currentQuestionResponse(getDataQuestion);
            return resp && typeof resp === 'object' ? resp.value : resp || '';
        },
        updateMeasurementResponse(getDataQuestion, value, unitId) {
            const unitType = this.getMeasurementUnitType(getDataQuestion);
            const unitObj = this.unitMeta[unitId] || {
                id: unitId,
                name: unitId,
                abbr: unitId,
                factor: 1,
                category: unitType
            };
            this.updateGetDataResponse({
                questionId: getDataQuestion._id,
                value: {
                    ...unitObj,
                    value
                },
                dataType: getDataQuestion.dataType
            });
        },
        onUnitTypeChange(getDataQuestion, type) {
            // Set the new type
            this.$set(getDataQuestion.additionalSettings, 'measurementUnitType', type);

            // Set the first unit of the new type
            const firstUnit = this.unitTypeList[type]?.[0] || '';

            // Set the new unit in additionalSettings
            this.$set(getDataQuestion.additionalSettings, 'measurementUnit', firstUnit);

            // Update the response with the new type and unit, and clear the value
            this.updateMeasurementResponse(getDataQuestion, '', firstUnit);

            // Also, clear any previous value in the response array (if present)
            const respIdx = this.activeQuestionResponse?.findIndex?.(r => r.id === getDataQuestion._id);
            if (respIdx !== undefined && respIdx !== -1) {
                this.$set(this.activeQuestionResponse, respIdx, {
                    id: getDataQuestion._id,
                    title: getDataQuestion.title,
                    dataType: getDataQuestion.dataType,
                    value: {
                        ...this.unitMeta[firstUnit],
                        value: ''
                    }
                });
            }

            this.$forceUpdate();
        },
        onUnitChange(getDataQuestion, unitId) {
            this.$set(getDataQuestion.additionalSettings, 'measurementUnit', unitId);
            this.updateMeasurementResponse(getDataQuestion, this.getMeasurementValue(getDataQuestion), unitId);
        },
        getMeasurementUnitType(getDataQuestion) {
            const resp = this.currentQuestionResponse(getDataQuestion);
            if (resp && typeof resp === 'object' && resp.category) {
                return resp.category;
            }
            return getDataQuestion.additionalSettings?.measurementUnitType || '';
        },
        getMeasurementUnit(getDataQuestion) {
            const resp = this.currentQuestionResponse(getDataQuestion);
            if (resp && typeof resp === 'object' && resp.id) return resp.id;
            if (typeof resp === 'string') return resp;
            return getDataQuestion.additionalSettings?.measurementUnit || '';
        },
        getCheckboxOptionGetDataResponses(option) {
            const response = (this.activeQuestionResponse || []).find(
                resp => resp?.value?.id === option._id
            );
            return response?.getDataResponses || [];
        },
        formatWithMoment(dateString) {
            if (!dateString) return '-';
            const parsed = moment(dateString);
            if (!parsed.isValid()) return '-';
            return formatLocaleDate(dateString, { withTime: true, uppercase: true });
        }
    },
};
</script>

<style lang="scss" scoped>
.survey-content {
    padding: 30px 20px;
}

.goal-title-container {
    width: 100%;
    max-width: 60%;
}

.goal-title {
    font-size: 24px;
    line-height: 1.4;
    margin: 0;
    word-wrap: break-word;
}

.question-heading {
    font-size: 18px;
    line-height: 1.4;
    color: #00bd73;
}

.question-text {
    font-size: 16px;
    line-height: 1.6;
    margin-bottom: 8px;
}

.arrow-button {
    background: #00bd73;
    border: 1px solid #00bd73;
    border-radius: 50%;
    width: 24px;
    height: 24px;
}

.survey-question {
    width: 100%;
    max-width: 96%;
    margin: 0 auto;
}

.repeat-read-only {
    pointer-events: none;
    opacity: 0.75;
}

.repeat-placeholder {
    border: 1px dashed #cfcfcf;
    border-radius: 6px;
    padding: 10px 12px;
    color: #6b6b6b;
    font-size: 13px;
    margin-bottom: 16px;
    background: #fafafa;
}

.repeat-sidebar {
    border: 1px solid #e3e3e3;
    border-radius: 8px;
    padding: 16px;
    background: #ffffff;
}

.repeat-title {
    font-size: 16px;
    font-weight: 600;
    color: #00bd73;
    margin: 0 0 12px 0;
}

.repeat-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.repeat-item {
    display: flex;
    align-items: center;
    width: 100%;
    border: 1px solid #dcdcdc;
    border-radius: 8px;
    padding: 12px;
    background: #ffffff;
    text-align: left;
    cursor: pointer;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.repeat-item--active {
    border-color: #00c48c;
    box-shadow: 0 0 0 1px rgba(0, 196, 140, 0.2);
}

.repeat-item--selected {
    border-color: #00c48c;
}

.repeat-item--submitted {
    border-color: #00c48c;
}

.repeat-item--upcoming {
    color: #9a9a9a;
    background: #f6f6f6;
}

.repeat-item--disabled {
    cursor: not-allowed;
}

.repeat-radio {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: 2px solid #00bd73;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 12px;
    flex-shrink: 0;
    background: #ffffff;
}

.repeat-radio-inner {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #00bd73;
}

.repeat-item-content {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.repeat-item-label {
    font-size: 13px;
    font-weight: 600;
    color: #333333;
}

.repeat-item-meta {
    font-size: 12px;
    color: #6b6b6b;
}

.repeat-rejected {
    color: #ff4d4f;
    font-weight: 600;
}

.navigation-btn {
    min-width: 120px;
}

.upload-document {
    width: 100%;
}

.dropZone {
    width: 90%;
    height: 175px;
    position: relative;
    border: 2px dashed #97a8b8;
}

.dropZone:hover {
    border: 2px solid $primary-color;
}

.dropZone-info {
    color: #a8a8a8;
    position: absolute;
    top: 50%;
    width: 100%;
    transform: translate(0, -50%);
    text-align: center;
    font-size: 12px;

    .underline {
        text-decoration: underline;
        color: #0057BD;
    }

    .blue-color {
        color: #0057BD;
    }
}


.dropZone input {
    position: absolute;
    cursor: pointer;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
}

.dropZone-over {
    background: #5c5c5c;
    opacity: 0.8;
}

.pdf-docs {
    display: flex;
    justify-content: start;
}

.pdf-box {
    height: 90px;
    width: 90px;
    background-color: #e5f8f1;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    border-radius: 4px;
}

.delete-icon {
    position: absolute;
    top: 5px;
    right: 5px;
    color: #135b00;
    cursor: pointer;
}

.file-extension {
    font-size: 18px;
    color: #135b00;
    margin: 0;
    line-height: 1;
}

.file-name {
    display: block;
    font-size: 12px;
    color: #333;
    margin-top: 5px;
    text-align: center;
    word-break: break-all;
}

.docs-box {
    display: flex;
    justify-content: center;
}

.docs-preview {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.img-item {
    display: flex;
    flex-direction: column;
    align-items: left;
}

.text-box {
    display: flex;
    align-items: center;
    width: 60%;
}

.signature-section {
    margin-top: 10px;
}

.signature-section img {
    border: 1px solid #e0e0e0;
    padding: 5px;
    background-color: #f5f5f5;
}

.question-description {
    font-size: 14px;
    line-height: 1.5;
    margin-bottom: 16px;
}

@media (max-width: 600px) {
    .survey-content {
        padding: 20px 10px;
    }

    .goal-title-container {
        max-width: 100%;
    }

    .goal-title {
        font-size: 20px;
    }

    .question-heading {
        font-size: 16px;
    }

    .question-text {
        font-size: 14px;
    }

    .arrow-button {
        width: 20px;
        height: 20px;
    }

    .navigation-btn {
        width: 100%;
        margin: 4px 0 !important;
    }

    .v-btn {
        width: 100%;
    }

    .pdf-box {
        height: 60px;
        width: 60px;
    }

    .file-extension {
        font-size: 14px;
    }

    .file-name {
        font-size: 10px;
    }

    .dropZone {
        height: 150px;
    }
}

.recommendation-text {
    color: #666;
    font-size: 14px;
    line-height: 1.6;
    border: 1px solid #FFB443;
    background-color: #FFFAEA;
    border-radius: 4px;
}


.rejection-block {
  border: 1px solid #ff4d4f;
  border-radius: 6px;
  padding: 12px 16px;
  background-color: #f8f0f0;
  font-family: sans-serif;
  margin-bottom: 20px;
}

.rejection-title {
  font-weight: 600;
  font-size: 14px;
  color: #ff4d4f;
  margin-bottom: 8px;
}

.rejection-reason {
  font-size: 13px;
  color: #333;
}

.rejection-reason span {
  font-weight: 600;
}
.radio-border{
    border:thin solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
    padding-top: 5px;
    padding-bottom: 5px;
    padding-left: 5px;
}
</style>
